import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  type LayoutChangeEvent,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';
import { Colors } from '../../themes';
import { PanelHeader } from '../crop-circle/Components/PanelHeader';
import { SlideAction, ViewMode } from './enums';
import {
  ColumnData,
  GrippPanelDebugState,
  PanelDebugElement,
  PanelDebugMeta,
  PanelDebugRect,
  PanelDebugRouteMatch,
  PanelDebugScrollMetrics,
  PanelDebugScrollableElement,
  PanelDebugSourceLocation,
  PanelDebugViewport,
  PanelRefDebug,
  PanelSlotDebug,
  SlideLayoutInitialTransition,
  SlideLayoutContextType,
  SlideLayoutProps,
  SlidePanelRegistrar,
  SlidePanelSlot,
} from './interfaces';

const SlideLayoutContext = createContext<SlideLayoutContextType | null>(null);

// Hint the browser about the properties this layout animates. `as never`
// bypasses RN style types for this web-only property.
const gpuLayer = {
  boxSizing: 'border-box',
  willChange: 'left, transform, width',
} as never;
const columnsContainerWebStyle = {
  overflowAnchor: 'none',
  overflowX: 'clip',
  overscrollBehaviorX: 'none',
} as never;
const scrollContainerWebStyle = {
  overflowAnchor: 'none',
} as never;

const LEFT_COLUMN_WIDTH_PERCENTAGE = 45;
const RIGHT_COLUMN_WIDTH_PERCENTAGE = 55;
const LEFT_COLUMN_GAP = 16;
const RIGHT_COLUMN_OFFSCREEN_TRANSLATE_PERCENTAGE = 100;
// Percentage fallback for route handoffs before container width is measured.
// Measured layouts keep LEFT_COLUMN_GAP pixels of the incoming panel visible.
const RIGHT_COLUMN_VISIBLE_EDGE_TRANSLATE_PERCENTAGE = 96;
// Reanimated Web may commit the final timing frame after a JS timeout scheduled
// for the same duration. Keep transformed route-handoff geometry alive briefly
// so the later layout-normalization pass cannot visibly snap at the end.
const LAYOUT_NORMALIZATION_SETTLE_DELAY = 80;
const PANEL_SCROLL_RESTORE_SAMPLE_INTERVAL_MS = 16;
const PANEL_SCROLL_RESTORE_SETTLE_BUFFER_MS = 100;
const RIGHT_COLUMN_AS_LEFT_TRANSLATE_PERCENTAGE =
  -(LEFT_COLUMN_WIDTH_PERCENTAGE / RIGHT_COLUMN_WIDTH_PERCENTAGE) * 100;
const LEFT_COLUMN_AS_RIGHT_TRANSLATE_PERCENTAGE =
  -RIGHT_COLUMN_AS_LEFT_TRANSLATE_PERCENTAGE;
const PANEL_DEBUG_MAX_SCROLLABLES_PER_SLOT = 25;

type PanelCaptureError = { error: string };
type PanelCaptureStatePayload = GrippPanelDebugState | null | PanelCaptureError;
type PanelCaptureHistoryPayload =
  | GrippPanelDebugState[]
  | null
  | PanelCaptureError;
type PanelStateTransitionLabel = 'forward' | 'back' | 'doubleBack';
type PanelStateTransitionPayload = {
  label: PanelStateTransitionLabel;
  capturedAt: string;
  before: PanelCaptureStatePayload;
  after: PanelCaptureStatePayload;
  history: PanelCaptureHistoryPayload;
};
type PanelStateTransitionMap = {
  forward: PanelStateTransitionPayload | null;
  back: PanelStateTransitionPayload | null;
  doubleBack: PanelStateTransitionPayload | null;
};
type PanelStateBundlePayload = {
  version: 2;
  generatedAt: string;
  // Backwards-compatible alias for transitions.forward.
  before: PanelCaptureStatePayload;
  after: PanelCaptureStatePayload;
  history: PanelCaptureHistoryPayload;
  transitions: PanelStateTransitionMap;
};
type PanelCaptureStateStart = () => PanelCaptureStatePayload;
type PanelCaptureStateCheckpoint = () => PanelStateTransitionPayload;
type PanelCaptureStateEnd = () => PanelStateBundlePayload;
type PanelCaptureRecordFrames = (
  durationMs?: number,
  filename?: string
) => void;
type PanelFrameContentNode = {
  nodeInstanceId: number;
  tagName: string;
  id?: string;
  testID?: string;
  role?: string;
  ariaLabel?: string;
  name?: string;
  type?: string;
  text?: string;
  textHash?: string;
  value?: string;
  valueHash?: string;
  valueLength?: number;
  checked?: boolean;
};
type PanelFrameSlotContent = {
  found: boolean;
  nodeInstanceId?: number;
  text?: string;
  textHash?: string;
  textLength?: number;
  childElementCount?: number;
  descendantElementCount?: number;
  dataTestIds?: string[];
  nodes?: PanelFrameContentNode[];
};
type PanelFrameSlot = {
  id?: string;
  title?: string;
  count?: number;
  visualPosition?: PanelSlotDebug['visualPosition'];
  visible?: boolean;
  widthPercent?: number;
  opacity?: number;
  translateXPercent?: number;
  rect?: PanelDebugRect;
  viewportRect?: PanelDebugRect;
  visibleViewport?: PanelDebugRect;
  visibleViewportRatio?: number;
  computedStyle?: PanelDebugElement['computedStyle'];
  scroll?: PanelDebugElement['scroll'];
  scrollables?: PanelDebugScrollableElement[];
  content?: PanelFrameSlotContent;
};
type PanelFrame = {
  t: number;
  capturedAt: string;
  pathname: string;
  search?: string;
  stateCapturedAt?: string;
  layout?: GrippPanelDebugState['layout'];
  router?: GrippPanelDebugState['router'];
  debug?: GrippPanelDebugState['debug'];
  browser?: GrippPanelDebugState['browser'];
  slots: Record<string, PanelFrameSlot>;
};
type PanelFrameSamplesPayload = {
  version: 1;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  startPath: string;
  startSearch?: string;
  endPath: string;
  endSearch?: string;
  frameCount: number;
  frames: PanelFrame[];
};
type PanelDebugWindow = {
  __GRIPP_PANEL_STATE_START__?: PanelCaptureStateStart;
  __GRIPP_PANEL_STATE_FORWARD__?: PanelCaptureStateCheckpoint;
  __GRIPP_PANEL_STATE_BACK__?: PanelCaptureStateCheckpoint;
  __GRIPP_PANEL_STATE_DOUBLE_BACK__?: PanelCaptureStateCheckpoint;
  __GRIPP_PANEL_STATE_END__?: PanelCaptureStateEnd;
  __GRIPP_PANEL_RECORD_FRAMES__?: PanelCaptureRecordFrames;
};

declare global {
  interface Window {
    __GRIPP_PANEL_STATE_START__?: PanelCaptureStateStart;
    __GRIPP_PANEL_STATE_FORWARD__?: PanelCaptureStateCheckpoint;
    __GRIPP_PANEL_STATE_BACK__?: PanelCaptureStateCheckpoint;
    __GRIPP_PANEL_STATE_DOUBLE_BACK__?: PanelCaptureStateCheckpoint;
    __GRIPP_PANEL_STATE_END__?: PanelCaptureStateEnd;
    __GRIPP_PANEL_RECORD_FRAMES__?: PanelCaptureRecordFrames;
  }
}

const isPanelDebugEnabled = () => true;

type PanelSlotName =
  | 'left'
  | 'right'
  | 'third'
  | 'fourth'
  | 'exitingLeft'
  | 'exitingRight';

const SLIDE_LAYOUT_TEST_ID = 'slide-layout';
const PANEL_SLOT_TEST_IDS: Record<PanelSlotName, string> = {
  left: 'slide-column-left',
  right: 'slide-column-right',
  third: 'slide-column-third',
  fourth: 'slide-column-fourth',
  exitingLeft: 'slide-column-exiting-left',
  exitingRight: 'slide-column-exiting-right',
};
const PANEL_SLOT_INSTANCE_SUFFIXES: Record<PanelSlotName, string> = {
  left: 'left',
  right: 'right',
  third: 'third',
  fourth: 'fourth',
  exitingLeft: 'exiting-left',
  exitingRight: 'exiting-right',
};
const PANEL_DEBUG_HISTORY_LIMIT = 20;
const PANEL_CAPTURE_DEFAULT_DURATION_MS = 2500;
const PANEL_STATE_BUNDLE_FILENAME = 'panel_state.json';
const PANEL_FRAME_TEXT_MAX_LENGTH = 1200;
const PANEL_FRAME_NODE_TEXT_MAX_LENGTH = 240;
const PANEL_FRAME_DATA_TEST_ID_LIMIT = 80;
const PANEL_FRAME_NODE_LIMIT = 80;
const PANEL_SCROLL_SNAPSHOT_LIMIT = 50;

const getPanelScrollRestoreSampleOffsets = (durationMs: number) => {
  const restoreUntil =
    durationMs +
    LAYOUT_NORMALIZATION_SETTLE_DELAY +
    PANEL_SCROLL_RESTORE_SETTLE_BUFFER_MS;
  const sampleOffsets: number[] = [];

  for (
    let delay = 0;
    delay <= restoreUntil;
    delay += PANEL_SCROLL_RESTORE_SAMPLE_INTERVAL_MS
  ) {
    sampleOffsets.push(delay);
  }

  if (sampleOffsets[sampleOffsets.length - 1] !== restoreUntil) {
    sampleOffsets.push(restoreUntil);
  }

  return sampleOffsets;
};

let currentPanelDebugState: GrippPanelDebugState | null = null;
let panelStateStartCapture: PanelCaptureStatePayload = null;
let panelStateForwardCapture: PanelStateTransitionPayload | null = null;
let panelStateBackCapture: PanelStateTransitionPayload | null = null;
let panelStateDoubleBackCapture: PanelStateTransitionPayload | null = null;
let panelDebugHistory: GrippPanelDebugState[] = [];
let panelStateHistoryStartIndex = 0;
let panelFrameNodeInstanceIds = new WeakMap<Element, number>();
let nextPanelFrameNodeInstanceId = 1;

const roundDebugNumber = (value: number) =>
  Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;

const createTestIdSelector = (testID: string) =>
  `[data-testid="${testID.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;

const getElementByDebugId = (debugId: string | undefined) => {
  if (!debugId || typeof document === 'undefined') return null;
  return document.getElementById(debugId);
};

const toRectDebug = (
  rect: Pick<
    DOMRect,
    'x' | 'y' | 'top' | 'right' | 'bottom' | 'left' | 'width' | 'height'
  >
): PanelDebugRect => {
  const left = roundDebugNumber(rect.left);
  const top = roundDebugNumber(rect.top);
  const width = roundDebugNumber(rect.width);
  const height = roundDebugNumber(rect.height);

  return {
    x: roundDebugNumber(rect.x),
    y: roundDebugNumber(rect.y),
    top,
    right: roundDebugNumber(rect.right),
    bottom: roundDebugNumber(rect.bottom),
    left,
    width,
    height,
    centerX: roundDebugNumber(left + width / 2),
    centerY: roundDebugNumber(top + height / 2),
  };
};

const offsetRectDebug = (
  rect: PanelDebugRect,
  offsetX: number,
  offsetY: number
): PanelDebugRect => {
  const x = rect.x + offsetX;
  const y = rect.y + offsetY;
  const left = rect.left + offsetX;
  const top = rect.top + offsetY;

  return {
    x: roundDebugNumber(x),
    y: roundDebugNumber(y),
    top: roundDebugNumber(top),
    right: roundDebugNumber(rect.right + offsetX),
    bottom: roundDebugNumber(rect.bottom + offsetY),
    left: roundDebugNumber(left),
    width: rect.width,
    height: rect.height,
    centerX: roundDebugNumber(rect.centerX + offsetX),
    centerY: roundDebugNumber(rect.centerY + offsetY),
  };
};

const createRectDebug = (
  left: number,
  top: number,
  width: number,
  height: number
): PanelDebugRect => {
  const normalizedLeft = roundDebugNumber(left);
  const normalizedTop = roundDebugNumber(top);
  const normalizedWidth = roundDebugNumber(width);
  const normalizedHeight = roundDebugNumber(height);

  return {
    x: normalizedLeft,
    y: normalizedTop,
    top: normalizedTop,
    right: roundDebugNumber(normalizedLeft + normalizedWidth),
    bottom: roundDebugNumber(normalizedTop + normalizedHeight),
    left: normalizedLeft,
    width: normalizedWidth,
    height: normalizedHeight,
    centerX: roundDebugNumber(normalizedLeft + normalizedWidth / 2),
    centerY: roundDebugNumber(normalizedTop + normalizedHeight / 2),
  };
};

const getVisibleViewportRect = (
  rect: PanelDebugRect,
  viewport: PanelDebugViewport
) => {
  const left = Math.max(0, Math.min(rect.left, viewport.width));
  const top = Math.max(0, Math.min(rect.top, viewport.height));
  const right = Math.max(left, Math.min(rect.right, viewport.width));
  const bottom = Math.max(top, Math.min(rect.bottom, viewport.height));
  const visibleViewport = createRectDebug(
    left,
    top,
    right - left,
    bottom - top
  );
  const area = rect.width * rect.height;
  const visibleArea = visibleViewport.width * visibleViewport.height;

  return {
    visibleViewport,
    visibleViewportRatio: area > 0 ? roundDebugNumber(visibleArea / area) : 0,
  };
};

const getViewportDebug = (): PanelDebugViewport | undefined => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined;
  }

  const documentElement = document.documentElement;
  const body = document.body;
  const width = window.innerWidth ?? documentElement?.clientWidth ?? 0;
  const height = window.innerHeight ?? documentElement?.clientHeight ?? 0;

  return {
    width: roundDebugNumber(width),
    height: roundDebugNumber(height),
    scrollX: roundDebugNumber(window.scrollX ?? window.pageXOffset ?? 0),
    scrollY: roundDebugNumber(window.scrollY ?? window.pageYOffset ?? 0),
    devicePixelRatio: roundDebugNumber(window.devicePixelRatio ?? 1),
    documentWidth: roundDebugNumber(
      Math.max(width, documentElement?.scrollWidth ?? 0, body?.scrollWidth ?? 0)
    ),
    documentHeight: roundDebugNumber(
      Math.max(
        height,
        documentElement?.scrollHeight ?? 0,
        body?.scrollHeight ?? 0
      )
    ),
  };
};

const getComputedStyleDebug = (
  element: Element
): PanelDebugElement['computedStyle'] | undefined => {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return undefined;
  }

  const style = window.getComputedStyle(element);
  return {
    display: style.display,
    visibility: style.visibility,
    opacity: style.opacity,
    pointerEvents: style.pointerEvents,
    position: style.position,
    zIndex: style.zIndex,
    transform: style.transform,
  };
};

type ScrollMetricsElement = Element & {
  scrollLeft: number;
  scrollTop: number;
  scrollWidth: number;
  scrollHeight: number;
  clientWidth: number;
  clientHeight: number;
};

const hasScrollMetrics = (
  element: Element
): element is ScrollMetricsElement => {
  const candidate = element as Element & Partial<ScrollMetricsElement>;

  return (
    typeof candidate.scrollLeft === 'number' &&
    typeof candidate.scrollTop === 'number' &&
    typeof candidate.scrollWidth === 'number' &&
    typeof candidate.scrollHeight === 'number' &&
    typeof candidate.clientWidth === 'number' &&
    typeof candidate.clientHeight === 'number'
  );
};

const getScrollMetricsDebug = (
  element: ScrollMetricsElement
): PanelDebugScrollMetrics => {
  const scrollLeft = roundDebugNumber(element.scrollLeft);
  const scrollTop = roundDebugNumber(element.scrollTop);
  const scrollWidth = roundDebugNumber(element.scrollWidth);
  const scrollHeight = roundDebugNumber(element.scrollHeight);
  const clientWidth = roundDebugNumber(element.clientWidth);
  const clientHeight = roundDebugNumber(element.clientHeight);
  const maxScrollLeft = roundDebugNumber(
    Math.max(0, scrollWidth - clientWidth)
  );
  const maxScrollTop = roundDebugNumber(
    Math.max(0, scrollHeight - clientHeight)
  );

  return {
    scrollLeft,
    scrollTop,
    scrollWidth,
    scrollHeight,
    clientWidth,
    clientHeight,
    maxScrollLeft,
    maxScrollTop,
    canScrollX: maxScrollLeft > 0,
    canScrollY: maxScrollTop > 0,
  };
};

const isRelevantScrollMetrics = (metrics: PanelDebugScrollMetrics) =>
  metrics.canScrollX ||
  metrics.canScrollY ||
  metrics.scrollLeft !== 0 ||
  metrics.scrollTop !== 0;

const getElementRectDebug = (
  element: Element,
  viewport: PanelDebugViewport,
  layoutRect?: PanelDebugRect,
  panelRect?: PanelDebugRect
): PanelDebugScrollableElement['rect'] | undefined => {
  if (typeof element.getBoundingClientRect !== 'function') return undefined;

  const viewportRect = toRectDebug(element.getBoundingClientRect());
  const { visibleViewport, visibleViewportRatio } = getVisibleViewportRect(
    viewportRect,
    viewport
  );
  const pageRect = offsetRectDebug(
    viewportRect,
    viewport.scrollX,
    viewport.scrollY
  );

  return {
    viewport: viewportRect,
    page: pageRect,
    ...(layoutRect
      ? {
          relativeToLayout: offsetRectDebug(
            viewportRect,
            -layoutRect.left,
            -layoutRect.top
          ),
        }
      : {}),
    ...(panelRect
      ? {
          relativeToPanel: offsetRectDebug(
            viewportRect,
            -panelRect.left,
            -panelRect.top
          ),
        }
      : {}),
    visibleViewport,
    visibleViewportRatio,
  };
};

const getScrollableSelectorHint = (element: Element, index: number) => {
  const testID = element.getAttribute('data-testid');
  if (testID) return createTestIdSelector(testID);
  if (element.id) return `#${element.id}`;

  return `${element.tagName.toLowerCase()}:scrollable(${index})`;
};

const getScrollableElementDebug = (
  element: ScrollMetricsElement,
  index: number,
  viewport: PanelDebugViewport,
  layoutRect?: PanelDebugRect,
  panelRect?: PanelDebugRect
): PanelDebugScrollableElement => {
  const testID = element.getAttribute('data-testid') || undefined;
  const role = element.getAttribute('role') || undefined;
  const ariaLabel = element.getAttribute('aria-label') || undefined;
  const className = element.className ? String(element.className) : undefined;

  return {
    index,
    tagName: element.tagName.toLowerCase(),
    selectorHint: getScrollableSelectorHint(element, index),
    ...(element.id ? { id: element.id } : {}),
    ...(testID ? { testID } : {}),
    ...(role ? { role } : {}),
    ...(ariaLabel ? { ariaLabel } : {}),
    ...(className ? { className } : {}),
    ...getScrollMetricsDebug(element),
    rect: getElementRectDebug(element, viewport, layoutRect, panelRect),
  };
};

const getPanelScrollableElementsDebug = (
  panelElement: Element,
  viewport: PanelDebugViewport,
  layoutRect?: PanelDebugRect,
  panelRect?: PanelDebugRect
) => {
  if (typeof panelElement.querySelectorAll !== 'function') return undefined;

  const candidates = [
    panelElement,
    ...Array.from(panelElement.querySelectorAll('*')),
  ].filter(hasScrollMetrics);
  const scrollables = candidates
    .map((element, index) =>
      getScrollableElementDebug(element, index, viewport, layoutRect, panelRect)
    )
    .filter((element) => isRelevantScrollMetrics(element))
    .slice(0, PANEL_DEBUG_MAX_SCROLLABLES_PER_SLOT);

  return scrollables.length > 0 ? scrollables : undefined;
};

const getElementDebug = (
  testID: string,
  viewport: PanelDebugViewport,
  layoutRect?: PanelDebugRect,
  debugId?: string
): PanelDebugElement => {
  const selector = createTestIdSelector(testID);

  if (typeof document === 'undefined' || !document.querySelector) {
    return {
      testID,
      selector,
      found: false,
      missingReason: 'document-unavailable',
    };
  }

  const element =
    getElementByDebugId(debugId) ?? document.querySelector(selector);
  if (!element || typeof element.getBoundingClientRect !== 'function') {
    return {
      testID,
      selector,
      found: false,
      missingReason: 'element-not-found',
    };
  }

  const rect = getElementRectDebug(element, viewport, layoutRect);
  const scroll = hasScrollMetrics(element)
    ? getScrollMetricsDebug(element)
    : undefined;

  return {
    testID,
    selector,
    found: true,
    rect,
    computedStyle: getComputedStyleDebug(element),
    ...(scroll && isRelevantScrollMetrics(scroll) ? { scroll } : {}),
    scrollables: getPanelScrollableElementsDebug(
      element,
      viewport,
      layoutRect,
      rect?.viewport
    ),
  };
};

const withBrowserPanelDebug = (
  state: GrippPanelDebugState
): GrippPanelDebugState => {
  const viewport = getViewportDebug();
  if (!viewport) return state;

  const layoutInstanceId = state.debug?.layoutInstanceId;
  const layoutElement = getElementDebug(
    SLIDE_LAYOUT_TEST_ID,
    viewport,
    undefined,
    layoutInstanceId
  );
  const layoutRect = layoutElement.rect?.viewport;
  const slots = { ...state.slots };

  (Object.keys(PANEL_SLOT_TEST_IDS) as PanelSlotName[]).forEach((slotName) => {
    const slot = slots[slotName];
    if (!slot) return;

    slots[slotName] = {
      ...slot,
      element: getElementDebug(
        PANEL_SLOT_TEST_IDS[slotName],
        viewport,
        layoutRect,
        layoutInstanceId
          ? `${layoutInstanceId}-${PANEL_SLOT_INSTANCE_SUFFIXES[slotName]}`
          : undefined
      ),
    };
  });

  return {
    ...state,
    slots,
    browser: {
      viewport,
      layoutElement,
    },
  };
};

const withLiveJsonSnapshot = (
  state: GrippPanelDebugState
): GrippPanelDebugState => {
  Object.defineProperty(state, 'toJSON', {
    configurable: true,
    enumerable: false,
    value: () => withBrowserPanelDebug(state),
  });

  return state;
};

const getPanelDebugWindow = () => {
  if (typeof window === 'undefined') return undefined;
  return window as Window & PanelDebugWindow;
};

const safeTimestamp = () => new Date().toISOString().replace(/[:.]/g, '-');

const normalizePanelCaptureFilename = (
  filename: string | undefined,
  fallbackBase: string
) => filename || `${fallbackBase}.${safeTimestamp()}.json`;

const downloadJson = (
  filename: string,
  data: unknown,
  options?: { pretty?: boolean }
) => {
  if (
    typeof document === 'undefined' ||
    !document.body ||
    typeof Blob === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return;
  }

  let url: string;
  try {
    const serialized =
      options?.pretty === false
        ? JSON.stringify(data)
        : JSON.stringify(data, null, 2);
    if (serialized === undefined) return;
    const blob = new Blob([serialized], {
      type: 'application/json',
    });
    url = URL.createObjectURL(blob);
  } catch {
    return;
  }

  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  if (typeof URL.revokeObjectURL === 'function') {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

const cloneJson = <T,>(value: T): T | PanelCaptureError => {
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch (error) {
    return { error: String(error) };
  }
};

const clonePanelState = (): PanelCaptureStatePayload =>
  cloneJson(currentPanelDebugState);

const isPanelCaptureState = (
  state: PanelCaptureStatePayload
): state is GrippPanelDebugState =>
  !!state &&
  typeof state === 'object' &&
  !('error' in state) &&
  'slots' in state;

const getPanelStateUrlKey = (state: GrippPanelDebugState) =>
  `${state.url.pathname}${state.url.search ?? ''}${state.url.hash ?? ''}`;

const getPanelStateVisiblePanelKey = (state: GrippPanelDebugState) =>
  Object.entries(state.slots)
    .filter(([, slot]) => slot?.visible !== false)
    .map(([slotName, slot]) => `${slotName}:${slot?.panel.id ?? ''}`)
    .join('|');

const findMatchingPanelHistoryIndex = (
  state: PanelCaptureStatePayload,
  fallbackIndex: number
) => {
  if (!isPanelCaptureState(state)) return fallbackIndex;
  if (panelDebugHistory.length === 0) return fallbackIndex;

  const urlKey = getPanelStateUrlKey(state);
  const visiblePanelKey = getPanelStateVisiblePanelKey(state);
  const lastCandidateIndex = Math.max(0, panelDebugHistory.length - 2);

  for (let index = lastCandidateIndex; index >= 0; index -= 1) {
    const candidate = panelDebugHistory[index];
    if (
      getPanelStateUrlKey(candidate) === urlKey &&
      getPanelStateVisiblePanelKey(candidate) === visiblePanelKey
    ) {
      return index;
    }
  }

  return fallbackIndex;
};

const clonePanelHistoryFrom = (
  startIndex: number
): PanelCaptureHistoryPayload => {
  const history = panelDebugHistory.slice(Math.max(0, startIndex));
  return cloneJson(history.length > 0 ? history : null);
};

const isPanelSlotName = (slotName: string): slotName is PanelSlotName =>
  Object.prototype.hasOwnProperty.call(PANEL_SLOT_TEST_IDS, slotName);

const normalizePanelFrameText = (value: string | null | undefined) =>
  (value ?? '').replace(/\s+/g, ' ').trim();

const truncatePanelFrameText = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

const hashPanelFrameText = (value: string) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (Math.imul(31, hash) + value.charCodeAt(index)) | 0;
  }

  return (hash >>> 0).toString(16).padStart(8, '0');
};

const getPanelFrameNodeInstanceId = (element: Element) => {
  const existingId = panelFrameNodeInstanceIds.get(element);
  if (existingId) return existingId;

  const nodeInstanceId = nextPanelFrameNodeInstanceId;
  nextPanelFrameNodeInstanceId += 1;
  panelFrameNodeInstanceIds.set(element, nodeInstanceId);

  return nodeInstanceId;
};

const getPanelFrameElementText = (element: Element) => {
  const candidate = element as HTMLElement & { innerText?: unknown };
  const text =
    typeof candidate.innerText === 'string'
      ? candidate.innerText
      : element.textContent;

  return normalizePanelFrameText(text);
};

const getPanelFrameElementValue = (element: Element) => {
  const value = (element as Element & { value?: unknown }).value;

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return undefined;
};

const getPanelFrameElementChecked = (element: Element) => {
  const checked = (element as Element & { checked?: unknown }).checked;
  return typeof checked === 'boolean' ? checked : undefined;
};

const getPanelFrameElementStringProperty = (
  element: Element,
  propertyName: 'name' | 'type'
) => {
  const value = (element as Element & Record<typeof propertyName, unknown>)[
    propertyName
  ];

  return typeof value === 'string' && value ? value : undefined;
};

const getPanelFrameContentNode = (element: Element): PanelFrameContentNode => {
  const text = getPanelFrameElementText(element);
  const value = getPanelFrameElementValue(element);
  const checked = getPanelFrameElementChecked(element);
  const id = element.id || undefined;
  const testID = element.getAttribute('data-testid') || undefined;
  const role = element.getAttribute('role') || undefined;
  const ariaLabel = element.getAttribute('aria-label') || undefined;
  const name = getPanelFrameElementStringProperty(element, 'name');
  const type = getPanelFrameElementStringProperty(element, 'type');

  return {
    nodeInstanceId: getPanelFrameNodeInstanceId(element),
    tagName: element.tagName.toLowerCase(),
    ...(id ? { id } : {}),
    ...(testID ? { testID } : {}),
    ...(role ? { role } : {}),
    ...(ariaLabel ? { ariaLabel } : {}),
    ...(name ? { name } : {}),
    ...(type ? { type } : {}),
    ...(text
      ? {
          text: truncatePanelFrameText(text, PANEL_FRAME_NODE_TEXT_MAX_LENGTH),
          textHash: hashPanelFrameText(text),
        }
      : {}),
    ...(value !== undefined
      ? {
          value: truncatePanelFrameText(
            value,
            PANEL_FRAME_NODE_TEXT_MAX_LENGTH
          ),
          valueHash: hashPanelFrameText(value),
          valueLength: value.length,
        }
      : {}),
    ...(checked !== undefined ? { checked } : {}),
  };
};

const getPanelFrameSlotElement = (
  slotName: string,
  layoutInstanceId?: string
) => {
  if (typeof document === 'undefined' || !isPanelSlotName(slotName)) {
    return null;
  }

  const debugId = layoutInstanceId
    ? `${layoutInstanceId}-${PANEL_SLOT_INSTANCE_SUFFIXES[slotName]}`
    : undefined;

  return (
    getElementByDebugId(debugId) ??
    document.querySelector(createTestIdSelector(PANEL_SLOT_TEST_IDS[slotName]))
  );
};

const isPanelFrameInterestingElement = (element: Element) => {
  const tagName = element.tagName.toLowerCase();

  return (
    !!element.id ||
    element.hasAttribute('data-testid') ||
    element.hasAttribute('role') ||
    element.hasAttribute('aria-label') ||
    element.getAttribute('contenteditable') === 'true' ||
    tagName === 'a' ||
    tagName === 'button' ||
    tagName === 'input' ||
    tagName === 'select' ||
    tagName === 'textarea'
  );
};

const getPanelFrameSlotContent = (
  slotName: string,
  layoutInstanceId?: string
): PanelFrameSlotContent => {
  const element = getPanelFrameSlotElement(slotName, layoutInstanceId);

  if (!element) return { found: false };

  const descendants = Array.from(element.querySelectorAll('*'));
  const elements = [element, ...descendants];
  const text = getPanelFrameElementText(element);
  const dataTestIds = elements
    .map((candidate) => candidate.getAttribute('data-testid'))
    .filter((testID): testID is string => !!testID)
    .slice(0, PANEL_FRAME_DATA_TEST_ID_LIMIT);
  const interestingElements = elements.filter(isPanelFrameInterestingElement);
  const nodeCandidates = (
    interestingElements.length > 0 ? interestingElements : elements
  ).slice(0, PANEL_FRAME_NODE_LIMIT);

  return {
    found: true,
    nodeInstanceId: getPanelFrameNodeInstanceId(element),
    ...(text
      ? { text: truncatePanelFrameText(text, PANEL_FRAME_TEXT_MAX_LENGTH) }
      : {}),
    textHash: hashPanelFrameText(text),
    textLength: text.length,
    childElementCount: element.childElementCount,
    descendantElementCount: descendants.length,
    ...(dataTestIds.length > 0 ? { dataTestIds } : {}),
    nodes: nodeCandidates.map(getPanelFrameContentNode),
  };
};

const cleanPanelFrameSlot = (
  slotName: string,
  slot: PanelSlotDebug,
  layoutInstanceId?: string
): PanelFrameSlot => ({
  id: slot.panel.id,
  title: slot.panel.title,
  count: slot.panel.count,
  visualPosition: slot.visualPosition,
  visible: slot.visible,
  widthPercent: slot.widthPercent,
  opacity: slot.opacity,
  translateXPercent: slot.translateXPercent,
  rect: slot.element?.rect?.relativeToLayout ?? slot.element?.rect?.viewport,
  viewportRect: slot.element?.rect?.viewport,
  visibleViewport: slot.element?.rect?.visibleViewport,
  visibleViewportRatio: slot.element?.rect?.visibleViewportRatio,
  computedStyle: slot.element?.computedStyle,
  scroll: slot.element?.scroll,
  scrollables: slot.element?.scrollables,
  content: getPanelFrameSlotContent(slotName, layoutInstanceId),
});

const getPanelCaptureNow = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const requestPanelCaptureFrame = (callback: () => void) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(callback);
    return;
  }

  setTimeout(callback, 16);
};

const collectPanelFrame = (startTime: number): PanelFrame => {
  const state = clonePanelState();
  const panelState = isPanelCaptureState(state) ? state : undefined;
  const locationTarget = getPanelDebugWindow()?.location;
  const layoutInstanceId = panelState?.debug?.layoutInstanceId;

  return {
    t: roundDebugNumber(getPanelCaptureNow() - startTime),
    capturedAt: new Date().toISOString(),
    pathname: locationTarget?.pathname ?? '',
    search: locationTarget?.search || undefined,
    stateCapturedAt: panelState?.capturedAt,
    layout: panelState?.layout,
    router: panelState?.router,
    debug: panelState?.debug,
    browser: panelState?.browser,
    slots: Object.fromEntries(
      Object.entries(panelState?.slots ?? {}).map(([name, slot]) => [
        name,
        cleanPanelFrameSlot(name, slot, layoutInstanceId),
      ])
    ),
  };
};

const resetPanelStateTransitionCaptures = () => {
  panelStateForwardCapture = null;
  panelStateBackCapture = null;
  panelStateDoubleBackCapture = null;
};

const resetPanelDebugSession = () => {
  currentPanelDebugState = null;
  panelStateStartCapture = null;
  panelDebugHistory = [];
  panelStateHistoryStartIndex = 0;
  resetPanelStateTransitionCaptures();
};

const capturePanelStateTransition = (
  label: PanelStateTransitionLabel,
  before: PanelCaptureStatePayload
): PanelStateTransitionPayload => {
  const historyStartIndex = findMatchingPanelHistoryIndex(
    before,
    panelStateHistoryStartIndex
  );
  const after = clonePanelState();
  const afterIndex =
    panelDebugHistory.length > 0
      ? panelDebugHistory.length - 1
      : panelDebugHistory.length;

  panelStateHistoryStartIndex = afterIndex;

  return {
    label,
    capturedAt: new Date().toISOString(),
    before,
    after,
    history: clonePanelHistoryFrom(historyStartIndex),
  };
};

const startPanelStateCapture: PanelCaptureStateStart = () => {
  const payload = clonePanelState();

  panelStateStartCapture = payload;
  panelDebugHistory = isPanelCaptureState(payload) ? [payload] : [];
  panelStateHistoryStartIndex = 0;
  resetPanelStateTransitionCaptures();
  console.info('captured panel_state before', payload);
  return payload;
};

const captureForwardPanelState: PanelCaptureStateCheckpoint = () => {
  const payload = capturePanelStateTransition(
    'forward',
    panelStateStartCapture
  );

  panelStateForwardCapture = payload;
  console.info('captured panel_state forward', payload);
  return payload;
};

const captureBackPanelState: PanelCaptureStateCheckpoint = () => {
  const payload = capturePanelStateTransition(
    'back',
    panelStateForwardCapture?.after ?? null
  );

  panelStateBackCapture = payload;
  console.info('captured panel_state back', payload);
  return payload;
};

const captureDoubleBackPanelState: PanelCaptureStateCheckpoint = () => {
  const payload = capturePanelStateTransition(
    'doubleBack',
    panelStateForwardCapture?.after ?? null
  );

  panelStateDoubleBackCapture = payload;
  console.info('captured panel_state doubleBack', payload);
  return payload;
};

const getForwardPanelStateCapture = () => {
  if (!panelStateForwardCapture) {
    panelStateForwardCapture = capturePanelStateTransition(
      'forward',
      panelStateStartCapture
    );
    console.info('captured panel_state forward', panelStateForwardCapture);
  }

  return panelStateForwardCapture;
};

const endPanelStateCapture: PanelCaptureStateEnd = () => {
  const forwardCapture = getForwardPanelStateCapture();
  const payload: PanelStateBundlePayload = {
    version: 2,
    generatedAt: new Date().toISOString(),
    before: forwardCapture.before,
    after: forwardCapture.after,
    history: forwardCapture.history,
    transitions: {
      forward: forwardCapture,
      back: panelStateBackCapture,
      doubleBack: panelStateDoubleBackCapture,
    },
  };

  downloadJson(PANEL_STATE_BUNDLE_FILENAME, payload, { pretty: false });
  console.info(`saved ${PANEL_STATE_BUNDLE_FILENAME}`, payload);
  return payload;
};

const recordPanelFrames: PanelCaptureRecordFrames = (
  durationMs = PANEL_CAPTURE_DEFAULT_DURATION_MS,
  filename
) => {
  const resolvedFilename = normalizePanelCaptureFilename(
    filename,
    'panel-frames'
  );
  const startedAt = new Date().toISOString();
  const locationTarget = getPanelDebugWindow()?.location;
  const startPath = locationTarget?.pathname ?? '';
  const startSearch = locationTarget?.search || undefined;
  const startTime = getPanelCaptureNow();
  const frames: PanelFrame[] = [];

  panelFrameNodeInstanceIds = new WeakMap<Element, number>();
  nextPanelFrameNodeInstanceId = 1;

  const finish = () => {
    const currentLocation = getPanelDebugWindow()?.location;
    const payload: PanelFrameSamplesPayload = {
      version: 1,
      startedAt,
      completedAt: new Date().toISOString(),
      durationMs,
      startPath,
      startSearch,
      endPath: currentLocation?.pathname ?? '',
      endSearch: currentLocation?.search || undefined,
      frameCount: frames.length,
      frames,
    };
    downloadJson(resolvedFilename, payload);
    console.info(`saved ${resolvedFilename}`, payload);
  };

  const tick = () => {
    frames.push(collectPanelFrame(startTime));

    if (getPanelCaptureNow() - startTime >= durationMs) {
      finish();
      return;
    }

    requestPanelCaptureFrame(tick);
  };

  console.info(
    `recording ${resolvedFilename} for ${durationMs}ms; perform the in-app trigger now`
  );
  requestPanelCaptureFrame(tick);
};

const installPanelCaptureHelpers = () => {
  if (!isPanelDebugEnabled()) return;

  const debugWindow = getPanelDebugWindow();
  if (!debugWindow) return;

  const shouldResetSession =
    !debugWindow.__GRIPP_PANEL_STATE_START__ ||
    !debugWindow.__GRIPP_PANEL_STATE_FORWARD__ ||
    !debugWindow.__GRIPP_PANEL_STATE_BACK__ ||
    !debugWindow.__GRIPP_PANEL_STATE_DOUBLE_BACK__ ||
    !debugWindow.__GRIPP_PANEL_STATE_END__ ||
    !debugWindow.__GRIPP_PANEL_RECORD_FRAMES__;

  if (shouldResetSession) resetPanelDebugSession();

  const legacyWindow = debugWindow as typeof debugWindow & {
    __GRIPP_SAVE_PANEL_STATE__?: unknown;
    __GRIPP_SAVE_PANEL_HISTORY__?: unknown;
    __GRIPP_PANEL_CAPTURE__?: unknown;
    __GRIPP_PANEL_HISTORY__?: unknown;
    __GRIPP_PANEL_FRAME_SAMPLES__?: unknown;
    __GRIPP_RECORD_PANEL_FRAMES__?: unknown;
  };

  delete legacyWindow.__GRIPP_SAVE_PANEL_STATE__;
  delete legacyWindow.__GRIPP_SAVE_PANEL_HISTORY__;
  delete legacyWindow.__GRIPP_PANEL_CAPTURE__;
  delete legacyWindow.__GRIPP_PANEL_HISTORY__;
  delete legacyWindow.__GRIPP_PANEL_FRAME_SAMPLES__;
  delete legacyWindow.__GRIPP_RECORD_PANEL_FRAMES__;

  debugWindow.__GRIPP_PANEL_STATE_START__ = startPanelStateCapture;
  debugWindow.__GRIPP_PANEL_STATE_FORWARD__ = captureForwardPanelState;
  debugWindow.__GRIPP_PANEL_STATE_BACK__ = captureBackPanelState;
  debugWindow.__GRIPP_PANEL_STATE_DOUBLE_BACK__ = captureDoubleBackPanelState;
  debugWindow.__GRIPP_PANEL_STATE_END__ = endPanelStateCapture;
  debugWindow.__GRIPP_PANEL_RECORD_FRAMES__ = recordPanelFrames;
};

const createPanelRefDebug = (
  column: ColumnData | null | undefined
): PanelRefDebug | undefined => {
  if (!column) return undefined;

  const debug = column.debug;
  const id = debug?.id ?? `title:${column.rawTitle ?? column.title}`;

  const ref: PanelRefDebug = { id };
  if (column.title) ref.title = column.title;
  if (column.rawTitle) ref.rawTitle = column.rawTitle;
  if (column.count !== undefined) ref.count = column.count;
  if (debug?.titleKey) ref.titleKey = debug.titleKey;
  if (debug?.component) ref.component = debug.component;
  if (debug?.componentSource) ref.componentSource = debug.componentSource;
  if (debug?.columnSource) ref.columnSource = debug.columnSource;
  if (debug?.ownerComponent) ref.ownerComponent = debug.ownerComponent;
  if (debug?.ownerSource) ref.ownerSource = debug.ownerSource;
  if (debug?.routePanelKey) ref.routePanelKey = debug.routePanelKey;
  if (debug?.routePattern) ref.routePattern = debug.routePattern;
  if (debug?.routeSource) ref.routeSource = debug.routeSource;
  if (debug?.source) ref.source = debug.source;
  if (debug?.entity) ref.entity = debug.entity;
  return ref;
};

const formatSourceLocation = (source: PanelDebugSourceLocation) =>
  source.line ? `${source.file}:${source.line}` : source.file;

const collectPanelDebugSourceHints = (
  routeMatches: PanelDebugRouteMatch[],
  debugMetas: Array<PanelDebugMeta | undefined>,
  extraFiles: string[] = []
) => {
  const exactFilesToOpen = new Set<string>();
  const exactIdsToSearch = new Set<string>();

  routeMatches.forEach((match) => {
    [match.routeSource, match.routeElementSource, match.shellSource].forEach(
      (source) => {
        if (source) exactFilesToOpen.add(source);
      }
    );
    [
      match.panelKey,
      match.panelTransitionKey,
      match.routePattern,
      match.routeElement,
      match.shellComponent,
    ].forEach((id) => {
      if (id) exactIdsToSearch.add(id);
    });
  });

  debugMetas.forEach((meta) => {
    if (!meta) return;
    [
      meta.componentSource,
      meta.columnSource,
      meta.ownerSource,
      meta.routeSource,
    ].forEach((source) => {
      if (source) exactFilesToOpen.add(source);
    });
    Object.values(meta.source ?? {}).forEach((source) => {
      if (source) exactFilesToOpen.add(formatSourceLocation(source));
      if (source?.symbol) exactIdsToSearch.add(source.symbol);
    });
    [
      meta.id,
      meta.titleKey,
      meta.rawTitle,
      meta.component,
      meta.ownerComponent,
      meta.routePanelKey,
      meta.routePattern,
    ].forEach((id) => {
      if (id) exactIdsToSearch.add(id);
    });
  });

  extraFiles.forEach((source) => exactFilesToOpen.add(source));

  return {
    exactFilesToOpen: Array.from(exactFilesToOpen),
    exactIdsToSearch: Array.from(exactIdsToSearch),
  };
};

const publishPanelDebugSnapshot = (state: GrippPanelDebugState) => {
  if (!isPanelDebugEnabled()) return;

  installPanelCaptureHelpers();

  const historyState = withBrowserPanelDebug(state);
  const publishedState = withLiveJsonSnapshot(withBrowserPanelDebug(state));
  const hasPublishedState = !!currentPanelDebugState;

  if (!hasPublishedState) {
    panelStateStartCapture = null;
    resetPanelStateTransitionCaptures();
  }

  const previousHistory = hasPublishedState ? panelDebugHistory : [];
  const history = [...previousHistory, historyState].slice(
    -PANEL_DEBUG_HISTORY_LIMIT
  );

  panelDebugHistory = history;
  currentPanelDebugState = publishedState;
};

installPanelCaptureHelpers();

type PendingExitPanel = {
  column: ColumnData;
  action: SlideAction;
  createdAt: number;
  headerTitle?: string;
  headerCount?: number;
  pairedColumn?: ColumnData;
  pairedHeaderTitle?: string;
  pairedHeaderCount?: number;
  pairedBehavior?: 'display-destination' | 'exit-with-panel';
  pairedScrollOffset?: number;
  pairedScrollSnapshot?: PanelScrollElementSnapshot[];
};

type TimeoutHandle = ReturnType<typeof setTimeout>;
type ScrollViewHandle = React.ElementRef<typeof ScrollView> & {
  getScrollableNode?: () => unknown;
  getNode?: () => unknown;
  scrollTo?: (options: { y: number; animated?: boolean }) => void;
};
type ScrollViewRefObject = React.RefObject<React.ElementRef<
  typeof ScrollView
> | null>;
type ScrollOffsetEvent = {
  currentTarget?: { scrollTop?: number };
  target?: { scrollTop?: number };
  nativeEvent?: {
    contentOffset?: { y?: number };
    target?: { scrollTop?: number };
  };
};
type PanelScrollElementSnapshot = PanelDebugScrollMetrics & {
  index: number;
  tagName: string;
  id?: string;
  testID?: string;
  role?: string;
  ariaLabel?: string;
  className?: string;
};

let pendingExitPanel: PendingExitPanel | null = null;
let nextSlideLayoutDebugId = 0;
let lastColumnsContainerWidth = 0;

const getColumnsContainerDomWidth = (layoutDebugId: string) => {
  const width = globalThis.document
    ?.getElementById(layoutDebugId)
    ?.getBoundingClientRect?.().width;

  return typeof width === 'number' && Number.isFinite(width) && width > 0
    ? width
    : 0;
};

const scheduleInitialTransitionAnimation = (callback: () => void) => {
  if (typeof globalThis.requestAnimationFrame === 'function') {
    let firstFrame: number | undefined;
    let secondFrame: number | undefined;

    firstFrame = globalThis.requestAnimationFrame(() => {
      firstFrame = undefined;
      secondFrame = globalThis.requestAnimationFrame(() => {
        secondFrame = undefined;
        callback();
      });
    });

    return () => {
      if (firstFrame !== undefined) {
        globalThis.cancelAnimationFrame?.(firstFrame);
      }
      if (secondFrame !== undefined) {
        globalThis.cancelAnimationFrame?.(secondFrame);
      }
    };
  }

  let secondTimeout: TimeoutHandle | undefined;
  const firstTimeout = setTimeout(() => {
    secondTimeout = setTimeout(callback, 0);
  }, 0);

  return () => {
    clearTimeout(firstTimeout);
    if (secondTimeout !== undefined) {
      clearTimeout(secondTimeout);
    }
  };
};

const setPendingExitPanel = (
  column: ColumnData,
  action: SlideAction,
  headerSnapshot?: { title?: string; count?: number },
  pairedSnapshot?: {
    column: ColumnData;
    headerSnapshot?: { title?: string; count?: number };
    behavior?: PendingExitPanel['pairedBehavior'];
    scrollOffset?: number;
    scrollSnapshot?: PanelScrollElementSnapshot[];
  }
) => {
  pendingExitPanel = {
    column,
    action,
    headerTitle: headerSnapshot?.title,
    headerCount: headerSnapshot?.count,
    pairedColumn: pairedSnapshot?.column,
    pairedHeaderTitle: pairedSnapshot?.headerSnapshot?.title,
    pairedHeaderCount: pairedSnapshot?.headerSnapshot?.count,
    pairedBehavior: pairedSnapshot?.behavior,
    pairedScrollOffset: pairedSnapshot?.scrollOffset,
    pairedScrollSnapshot: pairedSnapshot?.scrollSnapshot,
    createdAt: Date.now(),
  };
};

const hasScrollTopValue = (
  candidate: unknown
): candidate is { scrollTop: number } =>
  !!candidate &&
  typeof candidate === 'object' &&
  'scrollTop' in candidate &&
  typeof candidate.scrollTop === 'number';

const getScrollViewNode = (
  scrollViewRef: ScrollViewRefObject,
  nativeID?: string
) => {
  const scrollView = scrollViewRef.current as ScrollViewHandle | null;
  const domElement =
    nativeID && typeof globalThis.document !== 'undefined'
      ? globalThis.document.getElementById(nativeID)
      : null;
  const candidates: unknown[] = [
    scrollView?.getScrollableNode?.(),
    scrollView?.getNode?.(),
    scrollView,
    domElement,
  ];

  return candidates.find(hasScrollTopValue) ?? candidates.find(Boolean) ?? null;
};

const getScrollViewOffset = (
  scrollViewRef: ScrollViewRefObject,
  nativeID: string,
  fallbackOffset: number
) => {
  const scrollNode = getScrollViewNode(scrollViewRef, nativeID);

  if (hasScrollTopValue(scrollNode)) {
    return scrollNode.scrollTop;
  }

  return fallbackOffset;
};

const restoreScrollViewOffset = (
  scrollViewRef: ScrollViewRefObject,
  nativeID: string,
  offsetY: number,
  offsetRef: React.MutableRefObject<number>
) => {
  if (!Number.isFinite(offsetY)) return;

  offsetRef.current = offsetY;

  const scrollView = scrollViewRef.current as ScrollViewHandle | null;
  scrollView?.scrollTo?.({ y: offsetY, animated: false });

  const scrollNode = getScrollViewNode(scrollViewRef, nativeID);
  if (hasScrollTopValue(scrollNode)) {
    scrollNode.scrollTop = offsetY;
  }
};

const getScrollEventOffset = (event: ScrollOffsetEvent) =>
  event.nativeEvent?.contentOffset?.y ??
  event.currentTarget?.scrollTop ??
  event.target?.scrollTop ??
  event.nativeEvent?.target?.scrollTop;

const getPanelScrollSnapshotElement = (
  element: ScrollMetricsElement,
  index: number
): PanelScrollElementSnapshot => {
  const testID = element.getAttribute('data-testid') || undefined;
  const role = element.getAttribute('role') || undefined;
  const ariaLabel = element.getAttribute('aria-label') || undefined;
  const className = element.className ? String(element.className) : undefined;

  return {
    index,
    tagName: element.tagName.toLowerCase(),
    ...(element.id ? { id: element.id } : {}),
    ...(testID ? { testID } : {}),
    ...(role ? { role } : {}),
    ...(ariaLabel ? { ariaLabel } : {}),
    ...(className ? { className } : {}),
    ...getScrollMetricsDebug(element),
  };
};

const getPanelScrollElements = (
  layoutDebugId: string,
  slotName: PanelSlotName
) => {
  const slotElement = getPanelFrameSlotElement(slotName, layoutDebugId);
  if (!slotElement || typeof slotElement.querySelectorAll !== 'function') {
    return [];
  }

  return [slotElement, ...Array.from(slotElement.querySelectorAll('*'))].filter(
    hasScrollMetrics
  );
};

const getPanelScrollSnapshot = (
  layoutDebugId: string,
  slotName: PanelSlotName
) =>
  getPanelScrollElements(layoutDebugId, slotName)
    .map(getPanelScrollSnapshotElement)
    .filter((snapshot) => isRelevantScrollMetrics(snapshot))
    .slice(0, PANEL_SCROLL_SNAPSHOT_LIMIT);

const getPanelScrollElementIdentityScore = (
  element: ScrollMetricsElement,
  snapshot: PanelScrollElementSnapshot
) => {
  const testID = element.getAttribute('data-testid') || undefined;
  const role = element.getAttribute('role') || undefined;
  const ariaLabel = element.getAttribute('aria-label') || undefined;
  const className = element.className ? String(element.className) : undefined;
  let score = 0;

  if (snapshot.id && element.id === snapshot.id) score += 100;
  if (snapshot.testID && testID === snapshot.testID) score += 90;
  if (element.tagName.toLowerCase() === snapshot.tagName) score += 10;
  if (snapshot.className && className === snapshot.className) score += 25;
  if (snapshot.role && role === snapshot.role) score += 15;
  if (snapshot.ariaLabel && ariaLabel === snapshot.ariaLabel) score += 15;
  if (Math.abs(element.scrollHeight - snapshot.scrollHeight) <= 2) score += 10;
  if (Math.abs(element.scrollWidth - snapshot.scrollWidth) <= 2) score += 10;
  if (Math.abs(element.clientHeight - snapshot.clientHeight) <= 2) score += 5;
  if (Math.abs(element.clientWidth - snapshot.clientWidth) <= 2) score += 5;

  return score;
};

const findPanelScrollRestoreTarget = (
  candidates: ScrollMetricsElement[],
  snapshot: PanelScrollElementSnapshot,
  usedCandidates: Set<ScrollMetricsElement>
) => {
  const indexedCandidate = candidates[snapshot.index];
  if (indexedCandidate && !usedCandidates.has(indexedCandidate)) {
    const indexedScore = getPanelScrollElementIdentityScore(
      indexedCandidate,
      snapshot
    );
    if (indexedScore >= 20) return indexedCandidate;
  }

  let bestCandidate: ScrollMetricsElement | null = null;
  let bestScore = 0;

  candidates.forEach((candidate) => {
    if (usedCandidates.has(candidate)) return;

    const score = getPanelScrollElementIdentityScore(candidate, snapshot);
    if (score > bestScore) {
      bestScore = score;
      bestCandidate = candidate;
    }
  });

  return bestScore >= 20 ? bestCandidate : null;
};

const restorePanelScrollSnapshot = (
  layoutDebugId: string,
  slotName: PanelSlotName,
  snapshots: PanelScrollElementSnapshot[] | undefined
) => {
  if (!snapshots?.length) return;

  const candidates = getPanelScrollElements(layoutDebugId, slotName);
  const usedCandidates = new Set<ScrollMetricsElement>();

  snapshots.forEach((snapshot) => {
    const target = findPanelScrollRestoreTarget(
      candidates,
      snapshot,
      usedCandidates
    );
    if (!target) return;

    usedCandidates.add(target);
    target.scrollTop = snapshot.scrollTop;
    target.scrollLeft = snapshot.scrollLeft;
  });
};

const consumePendingExitPanel = (
  action: SlideAction
): PendingExitPanel | null => {
  if (!pendingExitPanel) return null;
  if (pendingExitPanel.action !== action) return null;
  if (Date.now() - pendingExitPanel.createdAt > 2000) {
    pendingExitPanel = null;
    return null;
  }

  const panel = pendingExitPanel;
  pendingExitPanel = null;
  return panel;
};

const getPanelEntityKey = (column: ColumnData | null | undefined) => {
  const entity = column?.debug?.entity;
  if (!entity?.type || !entity.id) return null;

  return `${entity.type}:${entity.id}`;
};

const isSamePanelEntity = (
  sourceColumn: ColumnData | null | undefined,
  destinationColumn: ColumnData | null | undefined
) => {
  const sourceEntityKey = getPanelEntityKey(sourceColumn);
  const destinationEntityKey = getPanelEntityKey(destinationColumn);

  return !!sourceEntityKey && sourceEntityKey === destinationEntityKey;
};

const withPreservedPanelContent = (
  destinationColumn: ColumnData | null | undefined,
  preservedColumn: ColumnData | null | undefined
): ColumnData | null => {
  if (!destinationColumn || !preservedColumn) return null;
  if (!isSamePanelEntity(preservedColumn, destinationColumn)) return null;

  return {
    ...destinationColumn,
    mainComponent: preservedColumn.mainComponent,
    debug: preservedColumn.debug ?? destinationColumn.debug,
  };
};

const toTranslatePercent = (value: number): `${number}%` =>
  `${value}%` as `${number}%`;

const clampUnit = (value: number) => Math.max(0, Math.min(1, value));

const getForwardHandoffProgress = (leftWidthPercent: number) =>
  clampUnit(
    (RIGHT_COLUMN_WIDTH_PERCENTAGE - leftWidthPercent) /
      (RIGHT_COLUMN_WIDTH_PERCENTAGE - LEFT_COLUMN_WIDTH_PERCENTAGE)
  );

const getBackwardHandoffProgress = (rightWidthPercent: number) =>
  clampUnit(
    (rightWidthPercent - LEFT_COLUMN_WIDTH_PERCENTAGE) /
      (RIGHT_COLUMN_WIDTH_PERCENTAGE - LEFT_COLUMN_WIDTH_PERCENTAGE)
  );

const getBackwardToThirdProgress = (thirdWidthPercent: number) =>
  clampUnit(
    (thirdWidthPercent - LEFT_COLUMN_WIDTH_PERCENTAGE) /
      (RIGHT_COLUMN_WIDTH_PERCENTAGE - LEFT_COLUMN_WIDTH_PERCENTAGE)
  );

const getThirdPanelProgress = (rightWidthPercent: number) =>
  clampUnit(
    (RIGHT_COLUMN_WIDTH_PERCENTAGE - rightWidthPercent) /
      (RIGHT_COLUMN_WIDTH_PERCENTAGE - LEFT_COLUMN_WIDTH_PERCENTAGE)
  );

const getTransitionStackDepth = (key: string | undefined) =>
  key ? key.split('>').filter(Boolean).length : 0;

export const useSlideLayout = () => {
  const context = useContext(SlideLayoutContext);
  if (!context) {
    throw new Error('useSlideLayout must be used within a SlideLayout');
  }
  return context;
};

// Safe version that returns null if used outside of a SlideLayout
export const useSlideLayoutSafe = () => {
  return useContext(SlideLayoutContext);
};

/**
 * Register a column for a slide slot from inside a child route element.
 *
 * Requires the enclosing `<SlideLayout>` to have `enableChildRoutes` set.
 * On mount / when `deps` change, the column is set. On unmount, it is cleared.
 * Pass `null` to explicitly un-register a slot (e.g. gated by a local flag)
 * without unmounting the component.
 */
export const useSlidePanel = (
  slot: SlidePanelSlot,
  column: ColumnData | null,
  deps: React.DependencyList
) => {
  const ctx = useOutletContext<SlidePanelRegistrar | null>();
  const setSlidePanel = ctx?.setSlidePanel;

  // Keep refs to the latest setter + slot so the unmount-only cleanup below
  // can clear the right slot without re-subscribing each render.
  const setSlidePanelRef = useRef(setSlidePanel);
  const slotRef = useRef(slot);
  useEffect(() => {
    setSlidePanelRef.current = setSlidePanel;
    slotRef.current = slot;
  }, [setSlidePanel, slot]);

  // Publish on mount / when deps change. No cleanup here — we deliberately
  // don't clear→re-set between renders because that briefly flips the slot
  // to null (extra parent render, risk of SINGLE↔DUAL flicker). Runs as a
  // layout effect so the shell's viewMode derivation sees the column in the
  // same commit the child route publishes it.
  useLayoutEffect(() => {
    if (!setSlidePanel) return;
    setSlidePanel(slot, column);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSlidePanel, slot, ...deps]);

  // Clear on unmount only, via the ref so a mid-lifetime setter swap doesn't
  // leave a stale slot populated. useLayoutEffect (not useEffect) so the
  // cleanup runs synchronously during commit, before a sibling route's
  // useLayoutEffect setup in the same commit. With useEffect this fired
  // after paint and clobbered whatever the newly-mounted sibling had just
  // published into the same slot (e.g. /assets/:id ↔ /assets/filters).
  useLayoutEffect(() => {
    return () => {
      setSlidePanelRef.current?.(slotRef.current, null);
    };
  }, []);
};

export const SlideLayout: React.FC<SlideLayoutProps> = ({
  leftColumn,
  rightColumn: rightColumnProp,
  thirdColumn: thirdColumnProp,
  fourthColumn: fourthColumnProp,
  viewMode: viewModeProp,
  onViewModeChange,
  initialTransition,
  transitionKey,
  enableChildRoutes,
  outletContext: outletContextExtras,
}) => {
  const layoutDebugIdRef = useRef<string | null>(null);
  if (!layoutDebugIdRef.current) {
    nextSlideLayoutDebugId += 1;
    layoutDebugIdRef.current = `gripp-slide-layout-${nextSlideLayoutDebugId}`;
  }
  const layoutDebugId = layoutDebugIdRef.current;

  // Slot state fed by child routes via `useSlidePanel`. The shell's prop
  // takes priority — it represents an explicit, user-driven override (e.g.
  // an assets selection panel) that should eclipse whatever route is
  // currently mounted. The outlet column is the URL-driven baseline used
  // whenever the shell hasn't supplied an override. Shells that need the
  // route panel to come back when their override clears (e.g. AssetsShell
  // does this when `clearSelection` runs) just stop passing the prop.
  const [outletRightColumn, setOutletRightColumn] = useState<ColumnData | null>(
    null
  );
  const [outletThirdColumn, setOutletThirdColumn] = useState<ColumnData | null>(
    null
  );
  const [outletFourthColumn, setOutletFourthColumn] =
    useState<ColumnData | null>(null);

  const setSlidePanel = useCallback(
    (slot: SlidePanelSlot, column: ColumnData | null) => {
      if (slot === 'right') setOutletRightColumn(column);
      else if (slot === 'third') setOutletThirdColumn(column);
      else if (slot === 'fourth') setOutletFourthColumn(column);
    },
    []
  );

  const rightColumn = rightColumnProp ?? outletRightColumn;
  const thirdColumn = thirdColumnProp ?? outletThirdColumn;
  const fourthColumn = fourthColumnProp ?? outletFourthColumn;

  // Derive DUAL from the resolved rightColumn when the prop is absent, so
  // shells don't have to keep writing `rightColumn ? DUAL : SINGLE`.
  const viewMode =
    viewModeProp ?? (rightColumn ? ViewMode.DUAL : ViewMode.SINGLE);

  const animationDuration = 300;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const direction = searchParams.get('direction');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialTransitionRef = useRef(initialTransition);
  const startsWithInitialDualTransition =
    initialTransitionRef.current === SlideAction.DUAL &&
    viewMode === ViewMode.DUAL &&
    !!rightColumn;
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(
    startsWithInitialDualTransition ? ViewMode.SINGLE : viewMode
  );
  const [isThirdToSingleTransitionActive, setIsThirdToSingleTransitionActive] =
    useState(false);
  const [
    isFourthDeepCloseTransitionActive,
    setIsFourthDeepCloseTransitionActive,
  ] = useState(false);
  const currentViewModeRef = useRef(currentViewMode);
  currentViewModeRef.current = currentViewMode;
  const [columnsContainerWidth, setColumnsContainerWidth] = useState(() =>
    initialTransition ? lastColumnsContainerWidth : 0
  );
  const [columnTitles, setColumnTitles] = useState<{
    left?: string;
    right?: string;
    third?: string;
    fourth?: string;
  }>({});
  const [columnCounts, setColumnCounts] = useState<{
    left?: number;
    right?: number;
    third?: number;
    fourth?: number;
  }>({});
  const columnTitlesRef = useRef(columnTitles);
  columnTitlesRef.current = columnTitles;
  const columnCountsRef = useRef(columnCounts);
  columnCountsRef.current = columnCounts;
  const [columnOverlays, setColumnOverlays] = useState<{
    left?: ReactNode;
    right?: ReactNode;
    third?: ReactNode;
    fourth?: ReactNode;
  }>({});
  const [exitingLeftPanel, setExitingLeftPanel] =
    useState<PendingExitPanel | null>(() =>
      initialTransitionRef.current === SlideAction.NEXT
        ? consumePendingExitPanel(SlideAction.NEXT)
        : null
    );
  const [exitingRightPanel, setExitingRightPanel] =
    useState<PendingExitPanel | null>(() =>
      initialTransitionRef.current === SlideAction.PREV
        ? consumePendingExitPanel(SlideAction.PREV)
        : null
    );
  const exitingLeftColumn = exitingLeftPanel?.column ?? null;
  const exitingRightColumn = exitingRightPanel?.column ?? null;
  const exitingLeftPreservedRightColumn =
    exitingLeftPanel?.pairedBehavior === 'display-destination'
      ? exitingLeftPanel.pairedColumn ?? null
      : null;
  const exitingLeftPairedScrollOffset = exitingLeftPanel?.pairedScrollOffset;
  const exitingLeftPairedScrollSnapshot =
    exitingLeftPanel?.pairedScrollSnapshot;
  const exitingRightPreservedLeftColumn =
    exitingRightPanel?.pairedBehavior === 'display-destination'
      ? exitingRightPanel.pairedColumn ?? null
      : null;
  const exitingRightPairedScrollOffset = exitingRightPanel?.pairedScrollOffset;
  const exitingRightPairedScrollSnapshot =
    exitingRightPanel?.pairedScrollSnapshot;
  const exitingRightPairedLeftColumn =
    exitingRightPanel?.pairedBehavior === 'exit-with-panel'
      ? exitingRightPanel.pairedColumn ?? null
      : null;
  const [
    settledForwardPreservedLeftColumn,
    setSettledForwardPreservedLeftColumn,
  ] = useState<ColumnData | null>(() =>
    isSamePanelEntity(exitingLeftPreservedRightColumn, leftColumn)
      ? exitingLeftPreservedRightColumn
      : null
  );
  const [
    settledBackwardPreservedThirdColumn,
    setSettledBackwardPreservedThirdColumn,
  ] = useState<ColumnData | null>(() =>
    isSamePanelEntity(exitingRightPreservedLeftColumn, thirdColumn)
      ? exitingRightPreservedLeftColumn
      : null
  );
  // Keep previous column content around during exit animations so it
  // doesn't unmount before the user can see the transition.
  const deferredRightColumn = useRef(rightColumn);
  if (rightColumn) {
    deferredRightColumn.current = rightColumn;
  }
  const deferredThirdColumn = useRef(thirdColumn);
  if (thirdColumn) {
    deferredThirdColumn.current = thirdColumn;
  }
  const deferredFourthColumn = useRef(fourthColumn);
  if (fourthColumn) {
    deferredFourthColumn.current = fourthColumn;
  }
  // Defense-in-depth for the concurrent-close gate in the 4th-column auto-hide
  // effect: reading from the ref guarantees the latest value regardless of
  // React batching semantics.
  const hasThirdColumnRef = useRef(!!thirdColumn);
  hasThirdColumnRef.current = !!thirdColumn;

  // Get current visible columns based on props (view mode only controls animations, not mounting)
  const visibleColumns = useMemo(
    () => ({
      left: leftColumn,
      right: deferredRightColumn.current || null,
      third: deferredThirdColumn.current || null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leftColumn, rightColumn, thirdColumn]
  );
  const visibleColumnsRef = useRef(visibleColumns);
  visibleColumnsRef.current = visibleColumns;

  const getInitialHorizontalOffset = (
    transition: SlideLayoutInitialTransition | undefined
  ) => {
    if (transition === SlideAction.NEXT) return 100;
    if (transition === SlideAction.PREV) return -100;
    return 0;
  };
  const wantsForwardTransition =
    initialTransitionRef.current === SlideAction.NEXT &&
    viewMode === ViewMode.DUAL;
  const wantsBackwardDualTransition =
    initialTransitionRef.current === SlideAction.PREV &&
    viewMode === ViewMode.DUAL;
  const hasThirdColumn = !!thirdColumn;
  const hasFourthColumn = !!fourthColumn;
  const hasFourthColumnRef = useRef(hasFourthColumn);
  hasFourthColumnRef.current = hasFourthColumn;
  const startsWithBackwardToSingleTransition =
    initialTransitionRef.current === SlideAction.PREV &&
    viewMode === ViewMode.SINGLE &&
    !!exitingRightColumn &&
    !!exitingRightPairedLeftColumn;
  const wantsBackwardTransition =
    wantsBackwardDualTransition || startsWithBackwardToSingleTransition;
  const startsWithForwardTransition = wantsForwardTransition && !!rightColumn;
  const startsWithBackwardTransition =
    wantsBackwardDualTransition && !!rightColumn;
  const startsWithForwardToThirdTransition =
    startsWithForwardTransition && !!thirdColumn && !!exitingLeftColumn;
  const initialHorizontalOffset =
    wantsForwardTransition || wantsBackwardTransition
      ? getInitialHorizontalOffset(initialTransitionRef.current)
      : 0;
  const startsWithConcurrentForwardTransition =
    startsWithForwardTransition &&
    !!exitingLeftColumn &&
    !startsWithForwardToThirdTransition;
  const startsWithBackwardToThirdTransition =
    startsWithBackwardTransition && !!thirdColumn && !!exitingRightColumn;
  const startsWithConcurrentBackwardTransition =
    startsWithBackwardTransition &&
    !!exitingRightColumn &&
    !startsWithBackwardToThirdTransition;
  // Backward two-column handoffs preserve the destination right panel as the
  // source left panel visually moving into the right slot.
  const initialStandardLeftColumnTranslateX = wantsBackwardTransition
    ? -100
    : initialHorizontalOffset;
  const initialStandardRightColumnTranslateX = wantsBackwardTransition
    ? -100
    : initialHorizontalOffset;
  const displaysPreservedForwardColumn =
    startsWithConcurrentForwardTransition && !!exitingLeftPreservedRightColumn;
  const displaysPreservedForwardRightColumn =
    startsWithForwardToThirdTransition && !!exitingLeftPreservedRightColumn;
  const displaysPreservedBackwardColumn =
    startsWithConcurrentBackwardTransition && !!exitingRightPreservedLeftColumn;
  const activeSettledForwardPreservedLeftColumn = isSamePanelEntity(
    settledForwardPreservedLeftColumn,
    leftColumn
  )
    ? settledForwardPreservedLeftColumn
    : null;
  const displayedLeftColumn =
    (displaysPreservedForwardColumn
      ? withPreservedPanelContent(
          leftColumn,
          exitingLeftPreservedRightColumn
        ) ?? exitingLeftPreservedRightColumn
      : withPreservedPanelContent(
          leftColumn,
          activeSettledForwardPreservedLeftColumn
        )) ?? visibleColumns.left;
  const displayedRightColumn = displaysPreservedForwardRightColumn
    ? withPreservedPanelContent(rightColumn, exitingLeftPreservedRightColumn) ??
      exitingLeftPreservedRightColumn
    : displaysPreservedBackwardColumn
    ? exitingRightPreservedLeftColumn
    : visibleColumns.right;
  const displayedThirdColumn =
    withPreservedPanelContent(thirdColumn, exitingRightPreservedLeftColumn) ??
    withPreservedPanelContent(
      thirdColumn,
      settledBackwardPreservedThirdColumn
    ) ??
    deferredThirdColumn.current;
  const displayedLeftHeaderTitle = displaysPreservedForwardColumn
    ? exitingLeftPanel?.pairedHeaderTitle
    : columnTitles.left;
  const displayedLeftHeaderCount = displaysPreservedForwardColumn
    ? exitingLeftPanel?.pairedHeaderCount
    : columnCounts.left;
  const displayedRightHeaderTitle = displaysPreservedForwardRightColumn
    ? exitingLeftPanel?.pairedHeaderTitle
    : displaysPreservedBackwardColumn
    ? exitingRightPanel?.pairedHeaderTitle
    : columnTitles.right;
  const displayedRightHeaderCount = displaysPreservedForwardRightColumn
    ? exitingLeftPanel?.pairedHeaderCount
    : displaysPreservedBackwardColumn
    ? exitingRightPanel?.pairedHeaderCount
    : columnCounts.right;
  const initialLeftColumnTranslateX = startsWithConcurrentForwardTransition
    ? LEFT_COLUMN_AS_RIGHT_TRANSLATE_PERCENTAGE
    : startsWithBackwardToThirdTransition
    ? 0
    : initialStandardLeftColumnTranslateX;
  const initialRightColumnTranslateX = startsWithConcurrentForwardTransition
    ? RIGHT_COLUMN_VISIBLE_EDGE_TRANSLATE_PERCENTAGE
    : startsWithBackwardToSingleTransition
    ? 0
    : startsWithBackwardToThirdTransition
    ? -100
    : initialStandardRightColumnTranslateX;
  const initialLeftColumnWidth = startsWithConcurrentForwardTransition
    ? RIGHT_COLUMN_WIDTH_PERCENTAGE
    : startsWithBackwardToSingleTransition
    ? 100
    : startsWithBackwardToThirdTransition
    ? 0
    : currentViewMode === ViewMode.SINGLE
    ? 100
    : LEFT_COLUMN_WIDTH_PERCENTAGE;
  const initialLeftColumnGapWidth = startsWithConcurrentForwardTransition
    ? 0
    : LEFT_COLUMN_GAP;
  const initialRightColumnTrailingGapWidth =
    startsWithConcurrentBackwardTransition ? LEFT_COLUMN_GAP : 0;
  const startsWithThirdPanelOpenTransition =
    hasThirdColumn && !startsWithBackwardToThirdTransition;

  // Animation values for smooth transitions
  const leftColumnTranslateX = useSharedValue(initialLeftColumnTranslateX);
  const rightColumnTranslateX = useSharedValue(initialRightColumnTranslateX);
  const leftColumnOpacity = useSharedValue(
    startsWithBackwardToThirdTransition ? 0 : 1
  );
  const rightColumnOpacity = useSharedValue(1);
  const leftColumnWidth = useSharedValue(initialLeftColumnWidth);
  const leftColumnGapWidth = useSharedValue(initialLeftColumnGapWidth);
  const rightColumnTrailingGapWidth = useSharedValue(
    initialRightColumnTrailingGapWidth
  );
  const rightColumnWidth = useSharedValue(
    startsWithInitialDualTransition
      ? RIGHT_COLUMN_WIDTH_PERCENTAGE
      : startsWithConcurrentForwardTransition || wantsBackwardTransition
      ? LEFT_COLUMN_WIDTH_PERCENTAGE
      : currentViewMode === ViewMode.DUAL
      ? RIGHT_COLUMN_WIDTH_PERCENTAGE
      : 0
  );
  const thirdColumnOpacity = useSharedValue(
    startsWithBackwardToThirdTransition || startsWithThirdPanelOpenTransition
      ? 1
      : 0
  );
  const thirdColumnWidth = useSharedValue(
    startsWithBackwardToThirdTransition
      ? LEFT_COLUMN_WIDTH_PERCENTAGE
      : startsWithThirdPanelOpenTransition
      ? RIGHT_COLUMN_WIDTH_PERCENTAGE
      : 0
  );
  const thirdColumnTranslateX = useSharedValue(
    startsWithBackwardToThirdTransition
      ? -100
      : startsWithThirdPanelOpenTransition
      ? 0
      : 100
  );
  const thirdToSingleProgress = useSharedValue(0);
  const isThirdPanelVisibleRef = useRef(startsWithBackwardToThirdTransition);
  const thirdAnimationFrameCancelRef = useRef<(() => void) | null>(null);
  const thirdCleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const fourthColumnOpacity = useSharedValue(hasFourthColumn ? 1 : 0);
  const fourthColumnWidth = useSharedValue(
    hasFourthColumn ? RIGHT_COLUMN_WIDTH_PERCENTAGE : 0
  );
  const fourthColumnTranslateX = useSharedValue(hasFourthColumn ? 0 : 100);
  const exitingLeftColumnTranslateX = useSharedValue(
    exitingLeftColumn ? 0 : -100
  );
  const exitingRightColumnTranslateX = useSharedValue(
    exitingRightColumn ? 0 : 100
  );
  const isFourthPanelVisibleRef = useRef(hasFourthColumn);
  const hasFourthPanelInFlight =
    hasFourthColumn ||
    isFourthPanelVisibleRef.current ||
    !!deferredFourthColumn.current;
  const hasExpandedFourthPanel =
    hasFourthColumn || isFourthPanelVisibleRef.current;
  const isFourthPanelDeepClose =
    isFourthDeepCloseTransitionActive ||
    (hasFourthPanelInFlight &&
      !hasThirdColumn &&
      !hasFourthColumn &&
      (isThirdPanelVisibleRef.current || !!deferredThirdColumn.current));
  const fourthTransitionGenerationRef = useRef(0);
  const fourthCleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const rightCleanupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const leftScrollViewRef = useRef<React.ElementRef<typeof ScrollView> | null>(
    null
  );
  const rightScrollViewRef = useRef<React.ElementRef<typeof ScrollView> | null>(
    null
  );
  const thirdScrollViewRef = useRef<React.ElementRef<typeof ScrollView> | null>(
    null
  );
  const leftScrollOffsetRef = useRef(0);
  const rightScrollOffsetRef = useRef(0);
  const thirdScrollOffsetRef = useRef(0);
  const pendingRightScrollRestoreRef = useRef<number | null>(null);
  const leftScrollRestoreTimeoutsRef = useRef<TimeoutHandle[]>([]);
  const rightScrollRestoreTimeoutsRef = useRef<TimeoutHandle[]>([]);
  const thirdScrollRestoreTimeoutsRef = useRef<TimeoutHandle[]>([]);
  const exitingLeftScrollRestoreTimeoutsRef = useRef<TimeoutHandle[]>([]);
  const initialTransitionCleanupTimeoutRef = useRef<TimeoutHandle | null>(null);
  const exitingLeftCleanupTimeoutRef = useRef<TimeoutHandle | null>(null);
  const exitingRightCleanupTimeoutRef = useRef<TimeoutHandle | null>(null);
  const thirdToSingleCleanupTimeoutRef = useRef<TimeoutHandle | null>(null);
  const transitionDebugSampleTimeoutsRef = useRef<TimeoutHandle[]>([]);
  const isMountedRef = useRef(true);
  const latestPanelDebugStateRef = useRef<GrippPanelDebugState | null>(null);
  const publishLatestPanelDebugState = useCallback(() => {
    const state = latestPanelDebugStateRef.current;
    if (!state) return;

    publishPanelDebugSnapshot({
      ...state,
      capturedAt: new Date().toISOString(),
    });
  }, []);
  const clearTransitionDebugSamples = useCallback(() => {
    transitionDebugSampleTimeoutsRef.current.forEach((timer) =>
      clearTimeout(timer)
    );
    transitionDebugSampleTimeoutsRef.current = [];
  }, []);
  const scheduleTransitionDebugSamples = useCallback(() => {
    if (!isPanelDebugEnabled()) return;

    clearTransitionDebugSamples();
    publishLatestPanelDebugState();

    const sampleOffsets = [
      50,
      100,
      150,
      200,
      250,
      animationDuration,
      animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY,
    ];
    transitionDebugSampleTimeoutsRef.current = sampleOffsets.map((delay) =>
      setTimeout(publishLatestPanelDebugState, delay)
    );
  }, [
    animationDuration,
    clearTransitionDebugSamples,
    publishLatestPanelDebugState,
  ]);
  const clearLeftScrollRestoreTimeouts = useCallback(() => {
    leftScrollRestoreTimeoutsRef.current.forEach((timer) =>
      clearTimeout(timer)
    );
    leftScrollRestoreTimeoutsRef.current = [];
  }, []);
  const clearRightScrollRestoreTimeouts = useCallback(() => {
    rightScrollRestoreTimeoutsRef.current.forEach((timer) =>
      clearTimeout(timer)
    );
    rightScrollRestoreTimeoutsRef.current = [];
  }, []);
  const clearThirdScrollRestoreTimeouts = useCallback(() => {
    thirdScrollRestoreTimeoutsRef.current.forEach((timer) =>
      clearTimeout(timer)
    );
    thirdScrollRestoreTimeoutsRef.current = [];
  }, []);
  const clearExitingLeftScrollRestoreTimeouts = useCallback(() => {
    exitingLeftScrollRestoreTimeoutsRef.current.forEach((timer) =>
      clearTimeout(timer)
    );
    exitingLeftScrollRestoreTimeoutsRef.current = [];
  }, []);
  const getLeftScrollOffset = useCallback(
    () =>
      getScrollViewOffset(
        leftScrollViewRef,
        `${layoutDebugId}-left-scroll`,
        leftScrollOffsetRef.current
      ),
    [layoutDebugId]
  );
  const getRightScrollOffset = useCallback(() => {
    return getScrollViewOffset(
      rightScrollViewRef,
      `${layoutDebugId}-right-scroll`,
      rightScrollOffsetRef.current
    );
  }, [layoutDebugId]);
  const getThirdScrollOffset = useCallback(
    () =>
      getScrollViewOffset(
        thirdScrollViewRef,
        `${layoutDebugId}-third-scroll`,
        thirdScrollOffsetRef.current
      ),
    [layoutDebugId]
  );
  const restoreLeftScrollOffset = useCallback(
    (offsetY: number) =>
      restoreScrollViewOffset(
        leftScrollViewRef,
        `${layoutDebugId}-left-scroll`,
        offsetY,
        leftScrollOffsetRef
      ),
    [layoutDebugId]
  );
  const restoreRightScrollOffset = useCallback(
    (offsetY: number) =>
      restoreScrollViewOffset(
        rightScrollViewRef,
        `${layoutDebugId}-right-scroll`,
        offsetY,
        rightScrollOffsetRef
      ),
    [layoutDebugId]
  );
  const restoreThirdScrollOffset = useCallback(
    (offsetY: number) =>
      restoreScrollViewOffset(
        thirdScrollViewRef,
        `${layoutDebugId}-third-scroll`,
        offsetY,
        thirdScrollOffsetRef
      ),
    [layoutDebugId]
  );
  const restoreLeftPanelScrollStateDuringTransition = useCallback(
    (
      offsetY: number | undefined,
      scrollSnapshot: PanelScrollElementSnapshot[] | undefined
    ) => {
      const resolvedOffsetY =
        typeof offsetY === 'number' && offsetY > 0 ? offsetY : undefined;
      const shouldRestoreScrollSnapshot = !!scrollSnapshot?.length;
      if (resolvedOffsetY === undefined && !shouldRestoreScrollSnapshot) return;

      clearLeftScrollRestoreTimeouts();

      const restore = () => {
        if (resolvedOffsetY !== undefined) {
          restoreLeftScrollOffset(resolvedOffsetY);
        }
        if (shouldRestoreScrollSnapshot) {
          restorePanelScrollSnapshot(layoutDebugId, 'left', scrollSnapshot);
        }
      };

      restore();

      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => {
          restore();
          globalThis.requestAnimationFrame(() => {
            restore();
          });
        });
      }

      const sampleOffsets = [
        0,
        50,
        100,
        150,
        animationDuration,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY + 50,
      ];
      leftScrollRestoreTimeoutsRef.current = sampleOffsets.map((delay) =>
        setTimeout(restore, delay)
      );
    },
    [
      animationDuration,
      clearLeftScrollRestoreTimeouts,
      layoutDebugId,
      restoreLeftScrollOffset,
    ]
  );
  const restoreThirdPanelScrollStateDuringTransition = useCallback(
    (
      offsetY: number | undefined,
      scrollSnapshot: PanelScrollElementSnapshot[] | undefined
    ) => {
      const resolvedOffsetY =
        typeof offsetY === 'number' && offsetY > 0 ? offsetY : undefined;
      const shouldRestoreScrollSnapshot = !!scrollSnapshot?.length;
      if (resolvedOffsetY === undefined && !shouldRestoreScrollSnapshot) return;

      clearThirdScrollRestoreTimeouts();

      const restore = () => {
        if (resolvedOffsetY !== undefined) {
          restoreThirdScrollOffset(resolvedOffsetY);
        }
        if (shouldRestoreScrollSnapshot) {
          restorePanelScrollSnapshot(layoutDebugId, 'third', scrollSnapshot);
        }
      };

      restore();

      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => {
          restore();
          globalThis.requestAnimationFrame(() => {
            restore();
          });
        });
      }

      const sampleOffsets = [
        0,
        50,
        100,
        150,
        animationDuration,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY + 50,
      ];
      thirdScrollRestoreTimeoutsRef.current = sampleOffsets.map((delay) =>
        setTimeout(restore, delay)
      );
    },
    [
      animationDuration,
      clearThirdScrollRestoreTimeouts,
      layoutDebugId,
      restoreThirdScrollOffset,
    ]
  );
  const restoreExitingLeftPanelScrollStateDuringTransition = useCallback(
    (scrollSnapshot: PanelScrollElementSnapshot[] | undefined) => {
      if (!scrollSnapshot?.length) return;

      clearExitingLeftScrollRestoreTimeouts();

      const restore = () => {
        restorePanelScrollSnapshot(
          layoutDebugId,
          'exitingLeft',
          scrollSnapshot
        );
      };

      restore();

      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => {
          restore();
          globalThis.requestAnimationFrame(() => {
            restore();
          });
        });
      }

      const sampleOffsets =
        getPanelScrollRestoreSampleOffsets(animationDuration);
      exitingLeftScrollRestoreTimeoutsRef.current = sampleOffsets.map((delay) =>
        setTimeout(restore, delay)
      );
    },
    [animationDuration, clearExitingLeftScrollRestoreTimeouts, layoutDebugId]
  );
  const restoreRightScrollOffsetDuringTransition = useCallback(
    (offsetY: number) => {
      if (offsetY <= 0) return;

      clearRightScrollRestoreTimeouts();
      restoreRightScrollOffset(offsetY);

      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => {
          restoreRightScrollOffset(offsetY);
          globalThis.requestAnimationFrame(() => {
            restoreRightScrollOffset(offsetY);
          });
        });
      }

      const sampleOffsets = [
        0,
        50,
        100,
        150,
        animationDuration,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY + 50,
      ];
      rightScrollRestoreTimeoutsRef.current = sampleOffsets.map((delay) =>
        setTimeout(() => restoreRightScrollOffset(offsetY), delay)
      );
    },
    [
      animationDuration,
      clearRightScrollRestoreTimeouts,
      restoreRightScrollOffset,
    ]
  );
  const restoreRightPanelScrollStateDuringTransition = useCallback(
    (
      offsetY: number | undefined,
      scrollSnapshot: PanelScrollElementSnapshot[] | undefined
    ) => {
      const resolvedOffsetY =
        typeof offsetY === 'number' && offsetY > 0 ? offsetY : undefined;
      const shouldRestoreScrollSnapshot = !!scrollSnapshot?.length;
      if (resolvedOffsetY === undefined && !shouldRestoreScrollSnapshot) return;

      clearRightScrollRestoreTimeouts();

      const restore = () => {
        if (resolvedOffsetY !== undefined) {
          restoreRightScrollOffset(resolvedOffsetY);
        }
        if (shouldRestoreScrollSnapshot) {
          restorePanelScrollSnapshot(layoutDebugId, 'right', scrollSnapshot);
        }
      };

      restore();

      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => {
          restore();
          globalThis.requestAnimationFrame(() => {
            restore();
          });
        });
      }

      const sampleOffsets = [
        0,
        50,
        100,
        150,
        animationDuration,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY,
        animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY + 50,
      ];
      rightScrollRestoreTimeoutsRef.current = sampleOffsets.map((delay) =>
        setTimeout(restore, delay)
      );
    },
    [
      animationDuration,
      clearRightScrollRestoreTimeouts,
      layoutDebugId,
      restoreRightScrollOffset,
    ]
  );
  const handleLeftScroll = useCallback(
    (event: ScrollOffsetEvent) => {
      const offsetY = getScrollEventOffset(event);
      leftScrollOffsetRef.current =
        typeof offsetY === 'number' && Number.isFinite(offsetY)
          ? offsetY
          : getLeftScrollOffset();
    },
    [getLeftScrollOffset]
  );
  const handleRightScroll = useCallback(
    (event: ScrollOffsetEvent) => {
      const offsetY = getScrollEventOffset(event);
      rightScrollOffsetRef.current =
        typeof offsetY === 'number' && Number.isFinite(offsetY)
          ? offsetY
          : getRightScrollOffset();
    },
    [getRightScrollOffset]
  );
  const handleThirdScroll = useCallback(
    (event: ScrollOffsetEvent) => {
      const offsetY = getScrollEventOffset(event);
      thirdScrollOffsetRef.current =
        typeof offsetY === 'number' && Number.isFinite(offsetY)
          ? offsetY
          : getThirdScrollOffset();
    },
    [getThirdScrollOffset]
  );

  // Prevents the prop-sync useEffect from canceling animations triggered internally
  const suppressPropSyncRef = useRef(false);
  const hasPreparedInitialTransitionFrameRef = useRef(false);
  const hasPreparedForwardToThirdTransitionFrameRef = useRef(false);
  const hasPreparedBackwardToThirdTransitionFrameRef = useRef(false);
  const hasPreparedFourthDeepCloseFrameRef = useRef(false);
  const hasRunInitialTransitionRef = useRef(false);
  const hasRunForwardToThirdTransitionRef = useRef(false);
  const hasRunBackwardToThirdTransitionRef = useRef(false);
  const hasRunExitTransitionRef = useRef(false);
  const usesMeasuredInitialTransitionDimensions =
    columnsContainerWidth > 0 &&
    (startsWithForwardTransition ||
      startsWithBackwardTransition ||
      startsWithBackwardToSingleTransition);
  const usesMeasuredConcurrentForwardTransition =
    columnsContainerWidth > 0 && startsWithConcurrentForwardTransition;
  const usesMeasuredConcurrentBackwardTransition =
    columnsContainerWidth > 0 &&
    startsWithBackwardTransition &&
    !!exitingRightColumn &&
    !startsWithBackwardToThirdTransition;
  const usesMeasuredPairedConcurrentBackwardTransition =
    columnsContainerWidth > 0 &&
    !!exitingRightColumn &&
    !!exitingRightPairedLeftColumn &&
    (startsWithBackwardTransition || startsWithBackwardToSingleTransition);
  const usesMeasuredBackwardToThirdTransition =
    columnsContainerWidth > 0 && startsWithBackwardToThirdTransition;
  const usesMeasuredBackwardToSingleTransition =
    columnsContainerWidth > 0 && startsWithBackwardToSingleTransition;
  const wantsMeasuredThirdPanelLayout =
    !startsWithBackwardToThirdTransition &&
    (!hasFourthPanelInFlight || isFourthPanelDeepClose) &&
    (!!thirdColumn ||
      isThirdPanelVisibleRef.current ||
      !!deferredThirdColumn.current);
  const usesMeasuredThirdPanelLayout =
    columnsContainerWidth > 0 && wantsMeasuredThirdPanelLayout;
  const usesMeasuredThirdToSingleLayout =
    columnsContainerWidth > 0 && isThirdToSingleTransitionActive;
  const usesMeasuredFourthPanelLayout =
    columnsContainerWidth > 0 &&
    hasFourthPanelInFlight &&
    !isFourthPanelDeepClose &&
    (!!thirdColumn ||
      isThirdPanelVisibleRef.current ||
      !!deferredThirdColumn.current);
  const usesMeasuredFourthExitLayout =
    columnsContainerWidth > 0 &&
    isFourthPanelDeepClose &&
    !!deferredFourthColumn.current;

  const handleColumnsContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const width = event.nativeEvent.layout.width;
      if (!Number.isFinite(width) || width <= 0) return;

      lastColumnsContainerWidth = width;
      setColumnsContainerWidth((current) =>
        Math.abs(current - width) < 0.5 ? current : width
      );
    },
    []
  );

  useLayoutEffect(() => {
    if (!wantsMeasuredThirdPanelLayout) return;

    const width = getColumnsContainerDomWidth(layoutDebugId);
    if (
      !width ||
      (columnsContainerWidth > 0 &&
        Math.abs(columnsContainerWidth - width) < 0.5)
    ) {
      return;
    }

    lastColumnsContainerWidth = width;
    setColumnsContainerWidth((current) =>
      Math.abs(current - width) < 0.5 ? current : width
    );
  }, [columnsContainerWidth, layoutDebugId, wantsMeasuredThirdPanelLayout]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const scheduleInitialAnimation = useCallback((callback: () => void) => {
    return scheduleInitialTransitionAnimation(() => {
      if (isMountedRef.current) callback();
    });
  }, []);

  const shouldPrepareForwardToThirdTransitionFrame =
    startsWithForwardToThirdTransition &&
    !hasPreparedForwardToThirdTransitionFrameRef.current &&
    !hasRunForwardToThirdTransitionRef.current;
  const shouldPrepareBackwardToThirdTransitionFrame =
    startsWithBackwardToThirdTransition &&
    !hasPreparedBackwardToThirdTransitionFrameRef.current &&
    !hasRunBackwardToThirdTransitionRef.current;

  if (!isFourthPanelDeepClose) {
    hasPreparedFourthDeepCloseFrameRef.current = false;
  }

  if (isFourthPanelDeepClose && !hasPreparedFourthDeepCloseFrameRef.current) {
    hasPreparedFourthDeepCloseFrameRef.current = true;

    leftColumnOpacity.value = 1;
    leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    leftColumnTranslateX.value = -100;
    rightColumnOpacity.value = 1;
    rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnTranslateX.value = -100;
    thirdColumnOpacity.value = 1;
    thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    thirdColumnTranslateX.value = RIGHT_COLUMN_AS_LEFT_TRANSLATE_PERCENTAGE;
    fourthColumnOpacity.value = 1;
    fourthColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    fourthColumnTranslateX.value = 0;
  }

  if (shouldPrepareForwardToThirdTransitionFrame) {
    // Child-route panels can register one slot at a time. When the destination
    // adds a third panel during a NEXT handoff, replace any provisional
    // two-panel seed with the B+C start frame before animated styles are read.
    hasPreparedInitialTransitionFrameRef.current = true;
    hasPreparedForwardToThirdTransitionFrameRef.current = true;

    isThirdPanelVisibleRef.current = true;
    leftColumnOpacity.value = 0;
    leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    leftColumnTranslateX.value = 0;
    leftColumnGapWidth.value = LEFT_COLUMN_GAP;
    rightColumnOpacity.value = 1;
    rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnTrailingGapWidth.value = 0;
    rightColumnTranslateX.value = 0;
    thirdColumnOpacity.value = 1;
    thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    thirdColumnTranslateX.value = 0;
    exitingLeftColumnTranslateX.value = 0;
  }

  if (shouldPrepareBackwardToThirdTransitionFrame) {
    // Child-route panels can register after the first destination render. If
    // the third column arrives late, seed the dedicated PREV handoff frame here
    // before animated styles are read so it does not fall through to the
    // ordinary "open third panel" animation.
    hasPreparedInitialTransitionFrameRef.current = true;
    hasPreparedBackwardToThirdTransitionFrameRef.current = true;

    isThirdPanelVisibleRef.current = true;
    leftColumnOpacity.value = 0;
    leftColumnWidth.value = 0;
    leftColumnTranslateX.value = 0;
    rightColumnOpacity.value = 1;
    rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnTranslateX.value = -100;
    thirdColumnOpacity.value = 1;
    thirdColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    thirdColumnTranslateX.value = -100;
    exitingRightColumnTranslateX.value = 0;
  }

  if (
    !hasPreparedInitialTransitionFrameRef.current &&
    !hasRunInitialTransitionRef.current &&
    (startsWithForwardTransition ||
      startsWithBackwardTransition ||
      startsWithBackwardToSingleTransition ||
      startsWithInitialDualTransition)
  ) {
    // Route panels registered through useSlidePanel arrive after the first
    // SlideLayout render. Seed the same start frame before animated styles are
    // created once those columns exist so Reanimated Web does not paint one
    // frame at the default settled geometry before the layout effect starts.
    hasPreparedInitialTransitionFrameRef.current = true;

    if (startsWithBackwardToThirdTransition) {
      isThirdPanelVisibleRef.current = true;
      leftColumnOpacity.value = 0;
      leftColumnWidth.value = 0;
      leftColumnTranslateX.value = 0;
      rightColumnOpacity.value = 1;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnTranslateX.value = -100;
      thirdColumnOpacity.value = 1;
      thirdColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      thirdColumnTranslateX.value = -100;
    } else if (startsWithBackwardToSingleTransition) {
      leftColumnWidth.value = 100;
      leftColumnGapWidth.value = LEFT_COLUMN_GAP;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = -100;
      rightColumnTranslateX.value = 0;
      exitingRightColumnTranslateX.value = 0;
    } else if (startsWithConcurrentForwardTransition) {
      if (typeof exitingLeftPairedScrollOffset === 'number') {
        leftScrollOffsetRef.current = exitingLeftPairedScrollOffset;
      }
      leftColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnGapWidth.value = 0;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = LEFT_COLUMN_AS_RIGHT_TRANSLATE_PERCENTAGE;
      rightColumnTranslateX.value =
        RIGHT_COLUMN_VISIBLE_EDGE_TRANSLATE_PERCENTAGE;
      exitingLeftColumnTranslateX.value = 0;
    } else if (startsWithInitialDualTransition) {
      leftColumnWidth.value = 100;
      leftColumnGapWidth.value = LEFT_COLUMN_GAP;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = 0;
      rightColumnTranslateX.value = 0;
    } else {
      leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnGapWidth.value = LEFT_COLUMN_GAP;
      rightColumnTrailingGapWidth.value = startsWithConcurrentBackwardTransition
        ? LEFT_COLUMN_GAP
        : 0;
      rightColumnWidth.value = startsWithBackwardTransition
        ? LEFT_COLUMN_WIDTH_PERCENTAGE
        : RIGHT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = initialStandardLeftColumnTranslateX;
      rightColumnTranslateX.value = initialStandardRightColumnTranslateX;
    }
  }

  const animateExitingRightColumn = useCallback(() => {
    if (!exitingRightColumn) return;

    if (exitingRightCleanupTimeoutRef.current) {
      clearTimeout(exitingRightCleanupTimeoutRef.current);
      exitingRightCleanupTimeoutRef.current = null;
    }

    hasRunExitTransitionRef.current = true;
    exitingRightColumnTranslateX.value = 0;
    exitingRightColumnTranslateX.value = withTiming(100, {
      duration: animationDuration,
    });

    exitingRightCleanupTimeoutRef.current = setTimeout(() => {
      setExitingRightPanel(null);
      exitingRightCleanupTimeoutRef.current = null;
    }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
  }, [
    animationDuration,
    exitingRightColumn,
    exitingRightColumnTranslateX,
    exitingRightCleanupTimeoutRef,
  ]);

  useLayoutEffect(() => {
    if (
      startsWithForwardToThirdTransition &&
      !hasRunForwardToThirdTransitionRef.current
    ) {
      const preservedRightScrollOffset = exitingLeftPairedScrollOffset;
      const preservedRightScrollSnapshot = exitingLeftPairedScrollSnapshot;

      hasRunInitialTransitionRef.current = true;
      hasRunForwardToThirdTransitionRef.current = true;
      suppressPropSyncRef.current = true;
      setCurrentViewMode(ViewMode.DUAL);

      isThirdPanelVisibleRef.current = true;
      leftColumnOpacity.value = 0;
      leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnTranslateX.value = 0;
      leftColumnGapWidth.value = LEFT_COLUMN_GAP;
      rightColumnOpacity.value = 1;
      rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnTranslateX.value = 0;
      thirdColumnOpacity.value = 1;
      thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      thirdColumnTranslateX.value = 0;
      exitingLeftColumnTranslateX.value = 0;

      let cleanupTimeout: TimeoutHandle | undefined;
      restoreRightPanelScrollStateDuringTransition(
        preservedRightScrollOffset,
        preservedRightScrollSnapshot
      );
      scheduleInitialAnimation(() => {
        scheduleTransitionDebugSamples();
        restoreRightPanelScrollStateDuringTransition(
          preservedRightScrollOffset,
          preservedRightScrollSnapshot
        );
        exitingLeftColumnTranslateX.value = withTiming(-100, {
          duration: animationDuration,
        });
        rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });

        cleanupTimeout = setTimeout(() => {
          leftColumnOpacity.value = 0;
          leftColumnWidth.value = 0;
          leftColumnTranslateX.value = 0;
          rightColumnOpacity.value = 1;
          rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
          rightColumnTranslateX.value = 0;
          thirdColumnOpacity.value = 1;
          thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
          thirdColumnTranslateX.value = 0;
          setExitingLeftPanel(null);
          cleanupTimeout = undefined;
        }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
      });

      return () => {
        if (cleanupTimeout !== undefined) {
          clearTimeout(cleanupTimeout);
        }
      };
    }

    if (
      startsWithBackwardToThirdTransition &&
      !hasRunBackwardToThirdTransitionRef.current
    ) {
      const preservedThirdScrollOffset = exitingRightPairedScrollOffset;
      const preservedThirdScrollSnapshot = exitingRightPairedScrollSnapshot;

      hasRunInitialTransitionRef.current = true;
      hasRunBackwardToThirdTransitionRef.current = true;
      suppressPropSyncRef.current = true;
      setCurrentViewMode(ViewMode.DUAL);

      // Reverse of the activity asset edit handoff: the edit form exits right,
      // the AssetCard shifts left-to-right, and the conversation enters left.
      isThirdPanelVisibleRef.current = true;
      leftColumnOpacity.value = 0;
      leftColumnWidth.value = 0;
      leftColumnTranslateX.value = 0;
      rightColumnOpacity.value = 1;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnTranslateX.value = -100;
      thirdColumnOpacity.value = 1;
      thirdColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      thirdColumnTranslateX.value = -100;
      exitingRightColumnTranslateX.value = 0;

      let settleTimeout: TimeoutHandle | undefined;
      restoreThirdPanelScrollStateDuringTransition(
        preservedThirdScrollOffset,
        preservedThirdScrollSnapshot
      );
      scheduleInitialAnimation(() => {
        scheduleTransitionDebugSamples();
        restoreThirdPanelScrollStateDuringTransition(
          preservedThirdScrollOffset,
          preservedThirdScrollSnapshot
        );
        rightColumnTranslateX.value = withTiming(0, {
          duration: animationDuration,
        });
        thirdColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
        thirdColumnTranslateX.value = withTiming(0, {
          duration: animationDuration,
        });
        animateExitingRightColumn();

        settleTimeout = setTimeout(() => {
          leftColumnOpacity.value = 0;
          leftColumnWidth.value = 0;
          leftColumnTranslateX.value = 0;
          rightColumnOpacity.value = 1;
          rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
          rightColumnTranslateX.value = 0;
          thirdColumnOpacity.value = 1;
          thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
          thirdColumnTranslateX.value = 0;
          setSettledBackwardPreservedThirdColumn(
            isSamePanelEntity(exitingRightPreservedLeftColumn, thirdColumn)
              ? exitingRightPreservedLeftColumn
              : null
          );
        }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
      });

      return () => {
        if (settleTimeout !== undefined) {
          clearTimeout(settleTimeout);
        }
      };
    }

    if (
      startsWithInitialDualTransition &&
      !hasRunInitialTransitionRef.current
    ) {
      hasRunInitialTransitionRef.current = true;
      suppressPropSyncRef.current = true;
      setCurrentViewMode(ViewMode.DUAL);

      leftColumnWidth.value = 100;
      leftColumnGapWidth.value = LEFT_COLUMN_GAP;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = 0;
      rightColumnTranslateX.value = 0;

      let settleTimeout: TimeoutHandle | undefined;
      scheduleInitialAnimation(() => {
        scheduleTransitionDebugSamples();
        leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });

        settleTimeout = setTimeout(() => {
          leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
          rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
          leftColumnTranslateX.value = 0;
          rightColumnTranslateX.value = 0;
        }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
      });

      return () => {
        if (settleTimeout !== undefined) {
          clearTimeout(settleTimeout);
        }
      };
    }

    if (hasRunInitialTransitionRef.current) return undefined;
    if (
      !startsWithForwardTransition &&
      !startsWithBackwardTransition &&
      !startsWithBackwardToSingleTransition
    ) {
      return undefined;
    }

    hasRunInitialTransitionRef.current = true;
    suppressPropSyncRef.current = true;
    setCurrentViewMode(
      startsWithBackwardToSingleTransition ? ViewMode.SINGLE : ViewMode.DUAL
    );

    if (startsWithBackwardToSingleTransition) {
      const pairedLeftScrollSnapshot = exitingRightPairedScrollSnapshot;

      leftColumnWidth.value = 100;
      leftColumnGapWidth.value = LEFT_COLUMN_GAP;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = -100;
      rightColumnTranslateX.value = 0;
      exitingRightColumnTranslateX.value = 0;
      hasRunExitTransitionRef.current = true;

      let cleanupTimeout: TimeoutHandle | undefined;
      restoreExitingLeftPanelScrollStateDuringTransition(
        pairedLeftScrollSnapshot
      );
      scheduleInitialAnimation(() => {
        scheduleTransitionDebugSamples();
        restoreExitingLeftPanelScrollStateDuringTransition(
          pairedLeftScrollSnapshot
        );
        leftColumnTranslateX.value = withTiming(0, {
          duration: animationDuration,
        });
        rightColumnTranslateX.value = withTiming(100, {
          duration: animationDuration,
        });
        exitingRightColumnTranslateX.value = withTiming(100, {
          duration: animationDuration,
        });

        cleanupTimeout = setTimeout(() => {
          leftColumnWidth.value = 100;
          leftColumnTranslateX.value = 0;
          rightColumnWidth.value = 0;
          rightColumnOpacity.value = 0;
          rightColumnTranslateX.value = 0;
          deferredRightColumn.current = null;
          setExitingRightPanel(null);
        }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
      });

      return () => {
        if (cleanupTimeout !== undefined) {
          clearTimeout(cleanupTimeout);
        }
      };
    }

    if (startsWithConcurrentForwardTransition) {
      const preservedLeftScrollOffset = exitingLeftPairedScrollOffset;
      const preservedLeftScrollSnapshot = exitingLeftPairedScrollSnapshot;

      leftColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnGapWidth.value = 0;
      rightColumnTrailingGapWidth.value = 0;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = LEFT_COLUMN_AS_RIGHT_TRANSLATE_PERCENTAGE;
      rightColumnTranslateX.value =
        RIGHT_COLUMN_VISIBLE_EDGE_TRANSLATE_PERCENTAGE;
      exitingLeftColumnTranslateX.value = 0;

      let cleanupTimeout: TimeoutHandle | undefined;
      restoreLeftPanelScrollStateDuringTransition(
        preservedLeftScrollOffset,
        preservedLeftScrollSnapshot
      );
      scheduleInitialAnimation(() => {
        scheduleTransitionDebugSamples();
        restoreLeftPanelScrollStateDuringTransition(
          preservedLeftScrollOffset,
          preservedLeftScrollSnapshot
        );
        exitingLeftColumnTranslateX.value = withTiming(-100, {
          duration: animationDuration,
        });
        leftColumnTranslateX.value = withTiming(0, {
          duration: animationDuration,
        });
        leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
        leftColumnGapWidth.value = withTiming(LEFT_COLUMN_GAP, {
          duration: animationDuration,
        });
        rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
        rightColumnTranslateX.value = withTiming(0, {
          duration: animationDuration,
        });

        cleanupTimeout = setTimeout(() => {
          leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
          leftColumnGapWidth.value = LEFT_COLUMN_GAP;
          leftColumnTranslateX.value = 0;
          rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
          rightColumnTranslateX.value = 0;
          setSettledForwardPreservedLeftColumn(
            isSamePanelEntity(exitingLeftPreservedRightColumn, leftColumn)
              ? exitingLeftPreservedRightColumn
              : null
          );
          setExitingLeftPanel(null);
        }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
      });

      return () => {
        if (cleanupTimeout !== undefined) {
          clearTimeout(cleanupTimeout);
        }
      };
    }

    leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    leftColumnGapWidth.value = LEFT_COLUMN_GAP;
    rightColumnTrailingGapWidth.value = startsWithConcurrentBackwardTransition
      ? LEFT_COLUMN_GAP
      : 0;
    rightColumnWidth.value = startsWithBackwardTransition
      ? LEFT_COLUMN_WIDTH_PERCENTAGE
      : RIGHT_COLUMN_WIDTH_PERCENTAGE;
    leftColumnOpacity.value = 1;
    rightColumnOpacity.value = 1;
    leftColumnTranslateX.value = initialStandardLeftColumnTranslateX;
    rightColumnTranslateX.value = initialStandardRightColumnTranslateX;
    if (startsWithBackwardTransition && exitingRightColumn) {
      hasRunExitTransitionRef.current = true;
    }
    if (exitingRightPairedLeftColumn) {
      restoreExitingLeftPanelScrollStateDuringTransition(
        exitingRightPairedScrollSnapshot
      );
    }

    scheduleInitialAnimation(() => {
      scheduleTransitionDebugSamples();
      if (exitingRightPairedLeftColumn) {
        restoreExitingLeftPanelScrollStateDuringTransition(
          exitingRightPairedScrollSnapshot
        );
      }
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      if (startsWithBackwardTransition) {
        rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
        if (startsWithConcurrentBackwardTransition) {
          rightColumnTrailingGapWidth.value = withTiming(0, {
            duration: animationDuration,
          });
        }
      }
      rightColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      if (startsWithBackwardTransition && exitingRightColumn) {
        animateExitingRightColumn();
      }
    });

    return undefined;
  }, [
    animationDuration,
    animateExitingRightColumn,
    exitingLeftPairedScrollOffset,
    exitingLeftPairedScrollSnapshot,
    exitingRightPairedScrollOffset,
    exitingRightPairedScrollSnapshot,
    initialHorizontalOffset,
    initialStandardLeftColumnTranslateX,
    initialStandardRightColumnTranslateX,
    exitingLeftColumnTranslateX,
    exitingLeftPreservedRightColumn,
    exitingRightColumn,
    exitingRightColumnTranslateX,
    exitingRightPairedLeftColumn,
    exitingRightPreservedLeftColumn,
    leftColumn,
    leftColumnOpacity,
    leftColumnGapWidth,
    leftColumnTranslateX,
    leftColumnWidth,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnTrailingGapWidth,
    rightColumnWidth,
    restoreLeftPanelScrollStateDuringTransition,
    restoreRightPanelScrollStateDuringTransition,
    restoreExitingLeftPanelScrollStateDuringTransition,
    restoreThirdPanelScrollStateDuringTransition,
    scheduleInitialAnimation,
    scheduleTransitionDebugSamples,
    startsWithConcurrentBackwardTransition,
    startsWithBackwardToSingleTransition,
    startsWithBackwardToThirdTransition,
    startsWithBackwardTransition,
    startsWithConcurrentForwardTransition,
    startsWithForwardToThirdTransition,
    startsWithForwardTransition,
    startsWithInitialDualTransition,
    thirdColumn,
    thirdColumnOpacity,
    thirdColumnTranslateX,
    thirdColumnWidth,
  ]);

  useLayoutEffect(() => {
    if (!exitingRightPairedLeftColumn) return;

    restoreExitingLeftPanelScrollStateDuringTransition(
      exitingRightPairedScrollSnapshot
    );
  }, [
    exitingRightPairedScrollSnapshot,
    exitingRightPairedLeftColumn,
    restoreExitingLeftPanelScrollStateDuringTransition,
  ]);

  useLayoutEffect(() => {
    if (!settledForwardPreservedLeftColumn) return;
    if (isSamePanelEntity(settledForwardPreservedLeftColumn, leftColumn)) {
      return;
    }

    setSettledForwardPreservedLeftColumn(null);
  }, [leftColumn, settledForwardPreservedLeftColumn]);

  useLayoutEffect(() => {
    if (!settledBackwardPreservedThirdColumn) return;
    if (isSamePanelEntity(settledBackwardPreservedThirdColumn, thirdColumn)) {
      return;
    }

    setSettledBackwardPreservedThirdColumn(null);
  }, [settledBackwardPreservedThirdColumn, thirdColumn]);

  useLayoutEffect(() => {
    if (!exitingRightColumn) return;
    if (hasRunExitTransitionRef.current) return;
    if (hasRunBackwardToThirdTransitionRef.current) return;
    if (!startsWithBackwardTransition) return;

    hasRunExitTransitionRef.current = true;
    animateExitingRightColumn();
  }, [
    animateExitingRightColumn,
    exitingRightColumn,
    startsWithBackwardTransition,
  ]);

  const switchToSingleMode = useCallback(() => {
    if (rightCleanupTimeoutRef.current) {
      clearTimeout(rightCleanupTimeoutRef.current);
      rightCleanupTimeoutRef.current = null;
    }
    if (thirdToSingleCleanupTimeoutRef.current) {
      clearTimeout(thirdToSingleCleanupTimeoutRef.current);
      thirdToSingleCleanupTimeoutRef.current = null;
    }

    if (
      isThirdPanelVisibleRef.current ||
      hasThirdColumn ||
      !!deferredThirdColumn.current
    ) {
      if (thirdAnimationFrameCancelRef.current) {
        thirdAnimationFrameCancelRef.current();
        thirdAnimationFrameCancelRef.current = null;
      }
      if (thirdCleanupTimeoutRef.current) {
        clearTimeout(thirdCleanupTimeoutRef.current);
        thirdCleanupTimeoutRef.current = null;
      }

      suppressPropSyncRef.current = true;
      isThirdPanelVisibleRef.current = false;
      setCurrentViewMode(ViewMode.SINGLE);
      setIsThirdToSingleTransitionActive(true);

      thirdToSingleProgress.value = 0;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      thirdColumnOpacity.value = 1;
      leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;

      thirdToSingleProgress.value = withTiming(1, {
        duration: animationDuration,
      });

      thirdToSingleCleanupTimeoutRef.current = setTimeout(() => {
        if (currentViewModeRef.current === ViewMode.SINGLE) {
          leftColumnWidth.value = 100;
          leftColumnTranslateX.value = 0;
          rightColumnWidth.value = 0;
          rightColumnOpacity.value = 0;
          rightColumnTranslateX.value = 0;
          thirdColumnWidth.value = 0;
          thirdColumnTranslateX.value = 100;
          thirdColumnOpacity.value = 1;
          deferredThirdColumn.current = null;
          setIsThirdToSingleTransitionActive(false);
        }
        thirdToSingleCleanupTimeoutRef.current = null;
      }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);

      onViewModeChange?.(ViewMode.SINGLE);
      return;
    }

    suppressPropSyncRef.current = true;
    setCurrentViewMode(ViewMode.SINGLE);

    // Keep the outgoing panel at its final geometry and opacity. The left
    // column expands from 45% to 100%, pushing the right panel out through the
    // clipped right edge without resizing or fading its content.
    rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnOpacity.value = 1;
    rightColumnTranslateX.value = 0;
    scheduleTransitionDebugSamples();
    leftColumnWidth.value = withTiming(100, { duration: animationDuration });

    rightCleanupTimeoutRef.current = setTimeout(() => {
      if (currentViewModeRef.current === ViewMode.SINGLE) {
        rightColumnWidth.value = 0;
        rightColumnOpacity.value = 0;
        rightColumnTranslateX.value = 0;
      }
      rightCleanupTimeoutRef.current = null;
    }, animationDuration);

    onViewModeChange?.(ViewMode.SINGLE);
  }, [
    animationDuration,
    onViewModeChange,
    hasThirdColumn,
    leftColumnOpacity,
    leftColumnWidth,
    leftColumnTranslateX,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnWidth,
    scheduleTransitionDebugSamples,
    thirdColumnOpacity,
    thirdColumnTranslateX,
    thirdColumnWidth,
    thirdToSingleProgress,
  ]);

  const switchToSingleModeImmediate = useCallback(() => {
    if (rightCleanupTimeoutRef.current) {
      clearTimeout(rightCleanupTimeoutRef.current);
      rightCleanupTimeoutRef.current = null;
    }

    setCurrentViewMode(ViewMode.SINGLE);
    rightColumnOpacity.value = 0;
    leftColumnWidth.value = 100;
    rightColumnWidth.value = 0;
    rightColumnTranslateX.value = 0;
    onViewModeChange?.(ViewMode.SINGLE);
  }, [
    onViewModeChange,
    leftColumnWidth,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnWidth,
  ]);

  const switchToDualMode = useCallback(() => {
    if (rightCleanupTimeoutRef.current) {
      clearTimeout(rightCleanupTimeoutRef.current);
      rightCleanupTimeoutRef.current = null;
    }

    suppressPropSyncRef.current = true;
    setCurrentViewMode(ViewMode.DUAL);

    // Keep the incoming panel at its final geometry while the left column
    // shrinks. This makes the panel enter from the right edge without
    // compressing its content or fading it in.
    leftColumnWidth.value = 100;
    leftColumnOpacity.value = 1;
    rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnOpacity.value = 1;
    rightColumnTranslateX.value = 0;
    scheduleTransitionDebugSamples();

    leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });

    onViewModeChange?.(ViewMode.DUAL);
  }, [
    animationDuration,
    onViewModeChange,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnWidth,
    scheduleTransitionDebugSamples,
  ]);

  const clearThirdPanelTimeout = useCallback(() => {
    if (thirdAnimationFrameCancelRef.current) {
      thirdAnimationFrameCancelRef.current();
      thirdAnimationFrameCancelRef.current = null;
    }
    if (thirdCleanupTimeoutRef.current) {
      clearTimeout(thirdCleanupTimeoutRef.current);
      thirdCleanupTimeoutRef.current = null;
    }
  }, []);

  const settleThirdPanelOpenLayout = useCallback(() => {
    leftColumnOpacity.value = 0;
    leftColumnWidth.value = 0;
    leftColumnTranslateX.value = 0;
    rightColumnOpacity.value = 1;
    rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnTranslateX.value = 0;
    thirdColumnOpacity.value = 1;
    thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    thirdColumnTranslateX.value = 0;
  }, [
    leftColumnOpacity,
    leftColumnTranslateX,
    leftColumnWidth,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnWidth,
    thirdColumnOpacity,
    thirdColumnTranslateX,
    thirdColumnWidth,
  ]);

  const settleThirdPanelClosedLayout = useCallback(
    (targetViewMode: ViewMode) => {
      leftColumnOpacity.value = 1;
      leftColumnTranslateX.value = 0;
      rightColumnTranslateX.value = 0;
      thirdColumnOpacity.value = 0;
      thirdColumnWidth.value = 0;
      thirdColumnTranslateX.value = 100;

      if (targetViewMode === ViewMode.DUAL) {
        rightColumnOpacity.value = 1;
        leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
        rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      } else {
        rightColumnOpacity.value = 0;
        leftColumnWidth.value = 100;
        rightColumnWidth.value = 0;
      }
    },
    [
      leftColumnOpacity,
      leftColumnTranslateX,
      leftColumnWidth,
      rightColumnOpacity,
      rightColumnTranslateX,
      rightColumnWidth,
      thirdColumnOpacity,
      thirdColumnTranslateX,
      thirdColumnWidth,
    ]
  );

  const openThirdPanel = useCallback(() => {
    const preservedRightScrollOffset =
      pendingRightScrollRestoreRef.current ?? getRightScrollOffset();
    pendingRightScrollRestoreRef.current = null;

    clearThirdPanelTimeout();
    isThirdPanelVisibleRef.current = true;

    // Start from the normal two-column geometry, then slide the window of
    // visible panels left while the middle panel shrinks into the left slot.
    leftColumnOpacity.value = 1;
    leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
    leftColumnTranslateX.value = 0;
    rightColumnOpacity.value = 1;
    rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    rightColumnTranslateX.value = 0;
    thirdColumnOpacity.value = 1;
    thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
    thirdColumnTranslateX.value = 0;

    scheduleTransitionDebugSamples();
    leftColumnTranslateX.value = withTiming(-100, {
      duration: animationDuration,
    });
    rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });
    rightColumnTranslateX.value = withTiming(-100, {
      duration: animationDuration,
    });
    thirdColumnTranslateX.value = withTiming(
      RIGHT_COLUMN_AS_LEFT_TRANSLATE_PERCENTAGE,
      { duration: animationDuration }
    );
    restoreRightScrollOffsetDuringTransition(preservedRightScrollOffset);

    thirdCleanupTimeoutRef.current = setTimeout(() => {
      settleThirdPanelOpenLayout();
      thirdCleanupTimeoutRef.current = null;
    }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
  }, [
    animationDuration,
    clearThirdPanelTimeout,
    getRightScrollOffset,
    leftColumnOpacity,
    leftColumnTranslateX,
    leftColumnWidth,
    restoreRightScrollOffsetDuringTransition,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnWidth,
    scheduleTransitionDebugSamples,
    settleThirdPanelOpenLayout,
    thirdColumnOpacity,
    thirdColumnTranslateX,
    thirdColumnWidth,
  ]);

  const closeThirdPanel = useCallback(
    (targetViewMode: ViewMode) => {
      clearThirdPanelTimeout();
      isThirdPanelVisibleRef.current = false;

      const settleClosedLayout = () => {
        settleThirdPanelClosedLayout(targetViewMode);
        deferredThirdColumn.current = null;
        setIsFourthDeepCloseTransitionActive(false);
        thirdCleanupTimeoutRef.current = null;
      };

      if (targetViewMode === ViewMode.DUAL) {
        // Recreate the current B+C visual state, then translate all three
        // panels right while the middle panel expands into the right slot.
        leftColumnOpacity.value = 1;
        leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
        leftColumnTranslateX.value = -100;
        rightColumnOpacity.value = 1;
        rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
        rightColumnTranslateX.value = -100;
        thirdColumnOpacity.value = 1;
        thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
        thirdColumnTranslateX.value = RIGHT_COLUMN_AS_LEFT_TRANSLATE_PERCENTAGE;

        thirdAnimationFrameCancelRef.current = scheduleInitialAnimation(() => {
          thirdAnimationFrameCancelRef.current = null;
          scheduleTransitionDebugSamples();
          leftColumnTranslateX.value = withTiming(0, {
            duration: animationDuration,
          });
          rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
            duration: animationDuration,
          });
          rightColumnTranslateX.value = withTiming(0, {
            duration: animationDuration,
          });
          thirdColumnTranslateX.value = withTiming(0, {
            duration: animationDuration,
          });

          thirdCleanupTimeoutRef.current = setTimeout(
            settleClosedLayout,
            animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY
          );
        });
      } else {
        scheduleTransitionDebugSamples();
        thirdColumnOpacity.value = withTiming(0, {
          duration: animationDuration,
        });
        thirdColumnWidth.value = withTiming(0, {
          duration: animationDuration,
        });
        thirdColumnTranslateX.value = withTiming(100, {
          duration: animationDuration,
        });
        leftColumnOpacity.value = withTiming(1, {
          duration: animationDuration,
        });
        rightColumnWidth.value = withTiming(0, {
          duration: animationDuration,
        });
        leftColumnWidth.value = withTiming(100, {
          duration: animationDuration,
        });

        thirdCleanupTimeoutRef.current = setTimeout(
          settleClosedLayout,
          animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY
        );
      }
    },
    [
      animationDuration,
      clearThirdPanelTimeout,
      leftColumnOpacity,
      leftColumnTranslateX,
      leftColumnWidth,
      rightColumnOpacity,
      rightColumnTranslateX,
      rightColumnWidth,
      scheduleInitialAnimation,
      scheduleTransitionDebugSamples,
      settleThirdPanelClosedLayout,
      thirdColumnOpacity,
      thirdColumnTranslateX,
      thirdColumnWidth,
    ]
  );

  const slideToNextColumn = useCallback(() => {
    suppressPropSyncRef.current = true;
    leftColumnOpacity.value = 1;
    rightColumnOpacity.value = 1;

    leftColumnTranslateX.value = withTiming(-100, {
      duration: animationDuration,
    });
    rightColumnTranslateX.value = withTiming(-100, {
      duration: animationDuration,
    });
    rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });

    setTimeout(() => {
      suppressPropSyncRef.current = false;
    }, animationDuration);
  }, [
    animationDuration,
    leftColumnOpacity,
    leftColumnTranslateX,
    rightColumnOpacity,
    rightColumnTranslateX,
    rightColumnWidth,
  ]);

  const slideToPreviousColumn = useCallback(() => {
    suppressPropSyncRef.current = true;
    rightColumnOpacity.value = 1;
    rightColumnTranslateX.value = withTiming(100, {
      duration: animationDuration,
    });

    setTimeout(() => {
      suppressPropSyncRef.current = false;
    }, animationDuration);
  }, [rightColumnOpacity, rightColumnTranslateX]);

  const fadeInRightColumns = useCallback(() => {
    rightColumnOpacity.value = 0;
    rightColumnOpacity.value = withTiming(1, {
      duration: animationDuration / 2,
    });
  }, [rightColumnOpacity]);

  const toggleThirdPanel = useCallback(() => {
    if (!thirdColumn) return;

    if (!isThirdPanelVisibleRef.current) {
      openThirdPanel();
    } else {
      closeThirdPanel(viewMode);
    }
  }, [thirdColumn, openThirdPanel, closeThirdPanel, viewMode]);

  const executeSlideAction = useCallback(
    (action?: SlideAction) => {
      switch (action) {
        case SlideAction.SINGLE:
          switchToSingleMode();
          break;
        case SlideAction.DUAL:
          switchToDualMode();
          break;
        case SlideAction.NEXT:
          slideToNextColumn();
          break;
        case SlideAction.PREV:
          slideToPreviousColumn();
          break;
        default:
          fadeInRightColumns();
          break;
      }
    },
    [
      switchToSingleMode,
      switchToDualMode,
      slideToNextColumn,
      slideToPreviousColumn,
      fadeInRightColumns,
    ]
  );

  // Sync viewMode prop to internal state before paint so route-registered
  // panels don't flash at the previous collapsed geometry for one frame.
  useLayoutEffect(() => {
    if (suppressPropSyncRef.current) {
      suppressPropSyncRef.current = false;
      return;
    }
    if (viewMode !== currentViewMode) {
      if (viewMode === ViewMode.DUAL) {
        // Wait for the new right column content before animating open.
        // Otherwise the panel animates in showing the previous asset's
        // stale content from `deferredRightColumn.current` (which isn't
        // refreshed until `rightColumn` is truthy — see the assignment
        // above), then swaps mid-animation once the new column lands.
        // When rightColumn arrives in a subsequent render, the effect
        // re-runs (rightColumn is in the deps) and fires the animation.
        if (!rightColumn) return;
        switchToDualMode();
      } else {
        if (!rightColumn && deferredRightColumn.current) {
          // rightColumn was removed — animate out, then clear deferred content
          switchToSingleMode();
          setTimeout(() => {
            deferredRightColumn.current = null;
          }, animationDuration);
        } else if (!rightColumn) {
          switchToSingleModeImmediate();
        } else {
          switchToSingleMode();
        }
      }
    }
  }, [
    viewMode,
    currentViewMode,
    rightColumn,
    switchToDualMode,
    switchToSingleMode,
    switchToSingleModeImmediate,
  ]);

  // Keep a stable ref to executeSlideAction to avoid re-firing on every render
  const executeSlideActionRef = useRef(executeSlideAction);
  useEffect(() => {
    executeSlideActionRef.current = executeSlideAction;
  });

  // Handle direction from URL params on load — only re-runs when direction actually changes
  useEffect(() => {
    if (!direction) return;

    const action = direction.toLowerCase() as SlideAction;
    executeSlideActionRef.current(action);
  }, [direction]);

  // When transitionKey's stack depth changes in DUAL mode, animate the column
  // transition. Same-depth key changes are content swaps within the current
  // slot, so they should not move columns.
  // useLayoutEffect runs before browser paint to avoid flickering on initial frame
  const prevTransitionKey = useRef(transitionKey);
  useLayoutEffect(() => {
    const previousTransitionKey = prevTransitionKey.current;
    if (transitionKey === previousTransitionKey) return;

    const previousTransitionDepth = getTransitionStackDepth(
      previousTransitionKey
    );
    const nextTransitionDepth = getTransitionStackDepth(transitionKey);
    prevTransitionKey.current = transitionKey;

    if (previousTransitionDepth === nextTransitionDepth) return;

    if (currentViewMode !== ViewMode.DUAL || !rightColumn) {
      return;
    }

    const isGoingBack = nextTransitionDepth < previousTransitionDepth;

    if (isGoingBack) {
      // Reset widths before sliding (slideToPreviousColumn may have altered them)
      leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      // Left column slides in from the left (activity list returns from left)
      leftColumnTranslateX.value = -100;
      rightColumnTranslateX.value = -100;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      rightColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
    } else {
      // Forward: left column (Issue) slides in from the right column position
      leftColumnTranslateX.value = 100;
      rightColumnTranslateX.value = 100;
      leftColumnOpacity.value = 1;
      rightColumnOpacity.value = 1;
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      rightColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
    }
  }, [
    animationDuration,
    transitionKey,
    currentViewMode,
    rightColumn,
    leftColumnTranslateX,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnTranslateX,
    rightColumnWidth,
    rightColumnOpacity,
  ]);

  // Auto-toggle third panel when thirdColumn prop appears/disappears.
  // Key the effect on the boolean presence so re-renders that only change
  // the thirdColumn object identity don't re-run this effect (which would
  // otherwise re-evaluate hadThird against a ref that was updated last run).
  useLayoutEffect(() => {
    if (hasThirdColumn && !isThirdPanelVisibleRef.current) {
      openThirdPanel();
    } else if (!hasThirdColumn && isThirdPanelVisibleRef.current) {
      // Restore left/right widths based on the incoming view mode so we
      // don't clobber a concurrent switchToSingleMode (e.g. user clicks
      // Back on the middle panel while the third panel is still open —
      // we need to end up in SINGLE mode with the list at 100%, not
      // DUAL at 45/55). Use the viewMode prop rather than currentViewMode
      // state: the state update from switchToSingleMode may not have
      // landed yet when this effect's closure is captured.
      closeThirdPanel(viewMode);
    }
  }, [hasThirdColumn, viewMode, openThirdPanel, closeThirdPanel]);

  // Auto-toggle fourth panel when fourthColumn prop appears/disappears. The
  // fourth column opens from the third-open state: the right column slides out
  // left at fixed width, the third column shrinks from 55% to 45%, and the
  // fourth column slides in from the right at fixed width and opacity.
  //
  // Invariant: when third and fourth close in the same commit, this effect must
  // not race the third-column close branch's ownership of right/third widths.
  useEffect(() => {
    if (hasFourthColumn && !isFourthPanelVisibleRef.current) {
      fourthTransitionGenerationRef.current += 1;
      clearThirdPanelTimeout();
      // Dev warning for malformed consumers — the 4th column has no
      // toggleFourthPanel fallback, so forgetting showBackButton/onBackButtonPress
      // ships a stuck panel.
      if (
        process.env.NODE_ENV !== 'production' &&
        fourthColumn &&
        (!fourthColumn.showBackButton || !fourthColumn.onBackButtonPress)
      ) {
        console.warn(
          '[SlideLayout] fourthColumn must set showBackButton: true and onBackButtonPress — there is no toggleFourthPanel fallback.'
        );
      }

      // Cancel any pending cleanup from a previous close animation so that
      // spam-clicking the "+" button doesn't blank the panel mid-re-open.
      if (fourthCleanupTimeoutRef.current) {
        clearTimeout(fourthCleanupTimeoutRef.current);
        fourthCleanupTimeoutRef.current = null;
      }
      isFourthPanelVisibleRef.current = true;

      rightColumnOpacity.value = 1;
      rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      thirdColumnOpacity.value = 1;
      thirdColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      fourthColumnOpacity.value = 1;
      fourthColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      fourthColumnTranslateX.value =
        RIGHT_COLUMN_OFFSCREEN_TRANSLATE_PERCENTAGE;
      scheduleTransitionDebugSamples();

      // Shift the edit panel from 55% to 45%. Measured styles derive the
      // right/fourth panel positions from this progress so those panels slide
      // without resizing or fading.
      thirdColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration,
      });

      fourthColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
    } else if (!hasFourthColumn && isFourthPanelVisibleRef.current) {
      const closeGeneration = ++fourthTransitionGenerationRef.current;
      isFourthPanelVisibleRef.current = false;

      const keepThirdPanelOpen = hasThirdColumnRef.current;

      fourthColumnOpacity.value = 1;
      fourthColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      fourthColumnTranslateX.value = 0;

      if (keepThirdPanelOpen) {
        rightColumnOpacity.value = 1;
        rightColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
        thirdColumnOpacity.value = 1;
        thirdColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      }

      scheduleTransitionDebugSamples();

      const closeFourthColumn = () => {
        fourthColumnTranslateX.value = withTiming(100, {
          duration: animationDuration,
        });
      };

      if (keepThirdPanelOpen) {
        closeFourthColumn();
      } else {
        scheduleInitialAnimation(closeFourthColumn);
      }

      // CRITICAL: only restore the third-open widths if third is still open.
      // When the user exits the edit panel entirely, both third AND fourth
      // auto-hide in the same commit; the third-column close branch above
      // already owns left/right widths and restores them based on `viewMode`.
      // We must not race it here. Read from the ref (not the closure-captured
      // hasFourthColumn deps) for defense-in-depth against any render-batching
      // surprises.
      if (keepThirdPanelOpen) {
        thirdColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
          duration: animationDuration,
        });
      }

      // Clear stale title so a future 4th-panel mode doesn't flash the
      // previous title.
      setColumnTitles((prev) => ({ ...prev, fourth: undefined }));

      fourthCleanupTimeoutRef.current = setTimeout(() => {
        if (
          fourthTransitionGenerationRef.current !== closeGeneration ||
          hasFourthColumnRef.current
        ) {
          return;
        }

        fourthColumnOpacity.value = 0;
        fourthColumnWidth.value = 0;
        fourthColumnTranslateX.value = 100;
        deferredFourthColumn.current = null;
        fourthCleanupTimeoutRef.current = null;
      }, animationDuration + LAYOUT_NORMALIZATION_SETTLE_DELAY);
    }
  }, [
    hasFourthColumn,
    fourthColumn,
    clearThirdPanelTimeout,
    rightColumnOpacity,
    rightColumnWidth,
    scheduleInitialAnimation,
    scheduleTransitionDebugSamples,
    thirdColumnOpacity,
    thirdColumnWidth,
    fourthColumnOpacity,
    fourthColumnWidth,
    fourthColumnTranslateX,
  ]);

  // Clear any pending column cleanup timeouts on SlideLayout
  // unmount so the callbacks can't fire into a dead component.
  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (rightCleanupTimeoutRef.current) {
        clearTimeout(rightCleanupTimeoutRef.current);
        rightCleanupTimeoutRef.current = null;
      }
      if (initialTransitionCleanupTimeoutRef.current) {
        clearTimeout(initialTransitionCleanupTimeoutRef.current);
        initialTransitionCleanupTimeoutRef.current = null;
      }
      if (exitingLeftCleanupTimeoutRef.current) {
        clearTimeout(exitingLeftCleanupTimeoutRef.current);
        exitingLeftCleanupTimeoutRef.current = null;
      }
      if (thirdCleanupTimeoutRef.current) {
        clearTimeout(thirdCleanupTimeoutRef.current);
        thirdCleanupTimeoutRef.current = null;
      }
      if (fourthCleanupTimeoutRef.current) {
        clearTimeout(fourthCleanupTimeoutRef.current);
        fourthCleanupTimeoutRef.current = null;
      }
      if (thirdAnimationFrameCancelRef.current) {
        thirdAnimationFrameCancelRef.current();
        thirdAnimationFrameCancelRef.current = null;
      }
      if (exitingRightCleanupTimeoutRef.current) {
        clearTimeout(exitingRightCleanupTimeoutRef.current);
        exitingRightCleanupTimeoutRef.current = null;
      }
      clearLeftScrollRestoreTimeouts();
      clearRightScrollRestoreTimeouts();
      clearThirdScrollRestoreTimeouts();
      clearExitingLeftScrollRestoreTimeouts();
      clearTransitionDebugSamples();
      if (thirdToSingleCleanupTimeoutRef.current) {
        clearTimeout(thirdToSingleCleanupTimeoutRef.current);
        thirdToSingleCleanupTimeoutRef.current = null;
      }
    },
    [
      clearLeftScrollRestoreTimeouts,
      clearRightScrollRestoreTimeouts,
      clearThirdScrollRestoreTimeouts,
      clearExitingLeftScrollRestoreTimeouts,
      clearTransitionDebugSamples,
    ]
  );

  const getHeaderSnapshot = useCallback(
    (slot: 'left' | 'right' | 'third' | 'fourth') => {
      return {
        title: columnTitlesRef.current[slot],
        count: columnCountsRef.current[slot],
      };
    },
    []
  );

  const prepareSlideTransition = useCallback(
    (action: SlideLayoutInitialTransition) => {
      const currentVisibleColumns = visibleColumnsRef.current;

      if (action === SlideAction.NEXT) {
        const hasVisibleThirdColumn =
          isThirdPanelVisibleRef.current && !!deferredThirdColumn.current;
        const exitSlot: 'left' | 'right' = hasVisibleThirdColumn
          ? 'right'
          : 'left';
        const preservedSlot: 'right' | 'third' = hasVisibleThirdColumn
          ? 'third'
          : 'right';
        const column = currentVisibleColumns[exitSlot];
        const preservedColumn =
          preservedSlot === 'third'
            ? deferredThirdColumn.current
            : currentVisibleColumns.right;
        if (!column) return;
        const preservedScrollOffset =
          preservedSlot === 'third'
            ? getThirdScrollOffset()
            : getRightScrollOffset();
        const preservedScrollSnapshot = getPanelScrollSnapshot(
          layoutDebugId,
          preservedSlot
        );
        if (preservedSlot === 'right') {
          pendingRightScrollRestoreRef.current = preservedScrollOffset;
        }
        setPendingExitPanel(
          column,
          action,
          getHeaderSnapshot(exitSlot),
          preservedColumn
            ? {
                column: preservedColumn,
                headerSnapshot: getHeaderSnapshot(preservedSlot),
                behavior: 'display-destination',
                scrollOffset: preservedScrollOffset,
                scrollSnapshot: preservedScrollSnapshot,
              }
            : undefined
        );
        return;
      }

      if (currentVisibleColumns.right) {
        setPendingExitPanel(
          currentVisibleColumns.right,
          action,
          getHeaderSnapshot('right')
        );
      }
    },
    [
      getHeaderSnapshot,
      getRightScrollOffset,
      getThirdScrollOffset,
      layoutDebugId,
    ]
  );

  const onSlideChange = useCallback(
    (action: SlideAction): Promise<void> => {
      return new Promise((resolve) => {
        executeSlideAction(action);

        setTimeout(() => {
          resolve();
        }, animationDuration);
      });
    },
    [executeSlideAction]
  );

  const handleBackPress = async (column: 'left' | 'right' | 'third') => {
    const currentVisibleColumns = visibleColumnsRef.current;
    const columnData = currentVisibleColumns[column];

    if (!columnData) {
      return;
    }

    const runBackButtonPress = () => {
      if (
        column === 'third' &&
        (hasFourthColumn ||
          isFourthPanelVisibleRef.current ||
          !!deferredFourthColumn.current)
      ) {
        setIsFourthDeepCloseTransitionActive(true);
      }

      columnData.onBackButtonPress?.();
    };

    if (columnData.backButtonGuard) {
      const allowed = await columnData.backButtonGuard();

      if (!allowed) {
        return;
      }
    }

    if (columnData.backButtonTransition) {
      if (columnData.backButtonTransitionBehavior === 'concurrent') {
        let exitSlot: 'left' | 'right' | 'third' = column;
        let exitColumn = columnData;
        let pairedSnapshot:
          | {
              column: ColumnData;
              headerSnapshot?: { title?: string; count?: number };
              behavior?: PendingExitPanel['pairedBehavior'];
              scrollOffset?: number;
              scrollSnapshot?: PanelScrollElementSnapshot[];
            }
          | undefined;

        if (columnData.backButtonTransitionExitColumn === 'both') {
          const visibleThirdColumn = isThirdPanelVisibleRef.current
            ? deferredThirdColumn.current
            : null;

          if (visibleThirdColumn && currentVisibleColumns.right) {
            exitSlot = 'third';
            exitColumn = visibleThirdColumn;
            pairedSnapshot = {
              column: currentVisibleColumns.right,
              headerSnapshot: getHeaderSnapshot('right'),
              behavior: 'exit-with-panel',
              scrollOffset: getRightScrollOffset(),
              scrollSnapshot: getPanelScrollSnapshot(layoutDebugId, 'right'),
            };
          } else {
            exitSlot = currentVisibleColumns.right ? 'right' : 'left';
            exitColumn = currentVisibleColumns[exitSlot] ?? columnData;
          }

          if (
            !pairedSnapshot &&
            currentVisibleColumns.left &&
            currentVisibleColumns.right
          ) {
            pairedSnapshot = {
              column: currentVisibleColumns.left,
              headerSnapshot: getHeaderSnapshot('left'),
              behavior: 'exit-with-panel',
              scrollOffset: getLeftScrollOffset(),
              scrollSnapshot: getPanelScrollSnapshot(layoutDebugId, 'left'),
            };
          }
        } else if (columnData.backButtonTransitionExitColumn) {
          exitSlot = columnData.backButtonTransitionExitColumn;
          exitColumn = currentVisibleColumns[exitSlot] ?? columnData;
        } else if (
          column === 'third' &&
          columnData.backButtonTransition === SlideAction.PREV
        ) {
          exitSlot = 'third';
          exitColumn = columnData;

          if (currentVisibleColumns.right) {
            pairedSnapshot = {
              column: currentVisibleColumns.right,
              headerSnapshot: getHeaderSnapshot('right'),
              behavior: 'display-destination',
              scrollOffset: getRightScrollOffset(),
              scrollSnapshot: getPanelScrollSnapshot(layoutDebugId, 'right'),
            };
          }
        } else if (
          columnData.backButtonTransition === SlideAction.PREV &&
          currentVisibleColumns.right
        ) {
          exitSlot = 'right';
          exitColumn = currentVisibleColumns.right;
        } else if (columnData.backButtonTransition === SlideAction.NEXT) {
          exitSlot =
            isThirdPanelVisibleRef.current && currentVisibleColumns.right
              ? 'right'
              : 'left';
          exitColumn = currentVisibleColumns[exitSlot] ?? columnData;
        }

        if (
          !pairedSnapshot &&
          !columnData.backButtonTransitionExitColumn &&
          columnData.backButtonTransition === SlideAction.PREV &&
          exitSlot === 'right' &&
          column === 'left' &&
          currentVisibleColumns.left &&
          currentVisibleColumns.right
        ) {
          pairedSnapshot = {
            column: currentVisibleColumns.left,
            headerSnapshot: getHeaderSnapshot('left'),
            behavior: 'exit-with-panel',
            scrollOffset: getLeftScrollOffset(),
            scrollSnapshot: getPanelScrollSnapshot(layoutDebugId, 'left'),
          };
        } else if (
          !pairedSnapshot &&
          columnData.backButtonTransition === SlideAction.PREV &&
          exitSlot === 'right' &&
          currentVisibleColumns.left &&
          currentVisibleColumns.right
        ) {
          pairedSnapshot = {
            column: currentVisibleColumns.left,
            headerSnapshot: getHeaderSnapshot('left'),
            behavior: 'display-destination',
            scrollOffset: getLeftScrollOffset(),
            scrollSnapshot: getPanelScrollSnapshot(layoutDebugId, 'left'),
          };
        }

        setPendingExitPanel(
          exitColumn,
          columnData.backButtonTransition,
          getHeaderSnapshot(exitSlot),
          pairedSnapshot
        );

        if (columnData.onBackButtonPress) {
          runBackButtonPress();
        } else {
          navigate(-1);
        }
        return;
      }

      await onSlideChange(columnData?.backButtonTransition);
    }

    if (columnData.onBackButtonPress) {
      runBackButtonPress();
      return;
    }

    if (!columnData.backButtonTransition) {
      if (column === 'third') {
        if (isThirdPanelVisibleRef.current) {
          toggleThirdPanel();
        }
        return;
      }

      if (currentViewMode === ViewMode.DUAL) {
        // Animate left column expanding to full width before navigating back
        await onSlideChange(SlideAction.SINGLE);
      } else {
        await onSlideChange(SlideAction.PREV);
      }
    }

    navigate(-1);
  };

  const setColumnTitle = useCallback(
    (
      column: 'left' | 'right' | 'third' | 'fourth',
      title: string | undefined
    ) => {
      setColumnTitles((prev) => ({ ...prev, [column]: title }));
    },
    []
  );

  const setColumnCount = useCallback(
    (
      column: 'left' | 'right' | 'third' | 'fourth',
      count: number | undefined
    ) => {
      setColumnCounts((prev) => ({ ...prev, [column]: count }));
    },
    []
  );

  const setColumnOverlay = useCallback(
    (
      column: 'left' | 'right' | 'third' | 'fourth',
      overlay: ReactNode | null
    ) => {
      setColumnOverlays((prev) => ({
        ...prev,
        [column]: overlay ?? undefined,
      }));
    },
    []
  );

  const leftColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'left' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('left', title),
      setColumnCount: (count: number | undefined) =>
        setColumnCount('left', count),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('left', overlay),
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    }),
    [
      setColumnTitle,
      setColumnCount,
      setColumnOverlay,
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    ]
  );

  const rightColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'right' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('right', title),
      setColumnCount: (count: number | undefined) =>
        setColumnCount('right', count),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('right', overlay),
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    }),
    [
      setColumnTitle,
      setColumnCount,
      setColumnOverlay,
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    ]
  );

  const thirdColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'third' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('third', title),
      setColumnCount: (count: number | undefined) =>
        setColumnCount('third', count),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('third', overlay),
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    }),
    [
      setColumnTitle,
      setColumnCount,
      setColumnOverlay,
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    ]
  );

  const fourthColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'fourth' as const,
      get currentViewMode() {
        return currentViewModeRef.current;
      },
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('fourth', title),
      setColumnCount: (count: number | undefined) =>
        setColumnCount('fourth', count),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('fourth', overlay),
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    }),
    [
      setColumnTitle,
      setColumnCount,
      setColumnOverlay,
      onSlideChange,
      prepareSlideTransition,
      toggleThirdPanel,
    ]
  );

  // Outlet context exposed to child route elements — includes the slide-panel
  // registrar plus any app-specific fields the shell wants to share with its
  // child routes. `setSlidePanel` is placed last so a consumer can't
  // accidentally override it via `outletContextExtras`.
  const outletContextValue = useMemo(
    () => ({ ...outletContextExtras, setSlidePanel }),
    [outletContextExtras, setSlidePanel]
  );

  const leftColumnAnimatedStyle = useAnimatedStyle(() => {
    const usesAbsoluteLayout =
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToSingleTransition ||
      usesMeasuredConcurrentBackwardTransition ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout;

    return {
      position: usesAbsoluteLayout ? 'absolute' : 'relative',
      top: usesAbsoluteLayout ? 0 : undefined,
      bottom: usesAbsoluteLayout ? 0 : undefined,
      left: usesMeasuredThirdToSingleLayout
        ? (-columnsContainerWidth *
            LEFT_COLUMN_WIDTH_PERCENTAGE *
            (1 - thirdToSingleProgress.value)) /
          100
        : usesMeasuredConcurrentForwardTransition
        ? (columnsContainerWidth *
            LEFT_COLUMN_WIDTH_PERCENTAGE *
            (1 - getForwardHandoffProgress(leftColumnWidth.value))) /
          100
        : usesMeasuredConcurrentBackwardTransition
        ? (-columnsContainerWidth *
            LEFT_COLUMN_WIDTH_PERCENTAGE *
            (1 - getBackwardHandoffProgress(rightColumnWidth.value))) /
          100
        : usesMeasuredBackwardToThirdTransition
        ? 0
        : usesMeasuredThirdPanelLayout
        ? (-columnsContainerWidth *
            LEFT_COLUMN_WIDTH_PERCENTAGE *
            getThirdPanelProgress(rightColumnWidth.value)) /
          100
        : 0,
      transform:
        usesMeasuredThirdToSingleLayout ||
        usesMeasuredConcurrentForwardTransition ||
        usesMeasuredConcurrentBackwardTransition ||
        usesMeasuredBackwardToThirdTransition ||
        usesMeasuredThirdPanelLayout
          ? [{ translateX: toTranslatePercent(0) }]
          : [
              {
                translateX: toTranslatePercent(leftColumnTranslateX.value),
              },
            ],
      opacity: leftColumnOpacity.value,
      width: usesMeasuredThirdToSingleLayout
        ? (columnsContainerWidth *
            (LEFT_COLUMN_WIDTH_PERCENTAGE +
              RIGHT_COLUMN_WIDTH_PERCENTAGE * thirdToSingleProgress.value)) /
          100
        : usesMeasuredThirdPanelLayout
        ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
        : usesMeasuredInitialTransitionDimensions
        ? (columnsContainerWidth * leftColumnWidth.value) / 100
        : `${leftColumnWidth.value}%`,
    };
  });

  const leftColumnGapAnimatedStyle = useAnimatedStyle(() => ({
    width: leftColumnGapWidth.value,
  }));

  const rightColumnAnimatedStyle = useAnimatedStyle(() => ({
    position:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToSingleTransition ||
      usesMeasuredConcurrentForwardTransition ||
      usesMeasuredConcurrentBackwardTransition ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout
        ? 'absolute'
        : startsWithConcurrentForwardTransition
        ? 'absolute'
        : 'relative',
    top:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToSingleTransition ||
      usesMeasuredConcurrentForwardTransition ||
      usesMeasuredConcurrentBackwardTransition ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout ||
      startsWithConcurrentForwardTransition
        ? 0
        : undefined,
    bottom:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToSingleTransition ||
      usesMeasuredConcurrentForwardTransition ||
      usesMeasuredConcurrentBackwardTransition ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout ||
      startsWithConcurrentForwardTransition
        ? 0
        : undefined,
    left: usesMeasuredFourthPanelLayout
      ? (-columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          getThirdPanelProgress(thirdColumnWidth.value)) /
        100
      : usesMeasuredBackwardToSingleTransition
      ? (columnsContainerWidth * rightColumnTranslateX.value) / 100
      : usesMeasuredThirdToSingleLayout
      ? columnsContainerWidth * thirdToSingleProgress.value
      : usesMeasuredConcurrentForwardTransition
      ? (columnsContainerWidth *
          (LEFT_COLUMN_WIDTH_PERCENTAGE *
            (1 - getForwardHandoffProgress(leftColumnWidth.value)) +
            leftColumnWidth.value)) /
          100 -
        LEFT_COLUMN_GAP * (1 - getForwardHandoffProgress(leftColumnWidth.value))
      : usesMeasuredConcurrentBackwardTransition
      ? (columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          getBackwardHandoffProgress(rightColumnWidth.value)) /
        100
      : usesMeasuredBackwardToThirdTransition
      ? (-columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          (1 - getBackwardToThirdProgress(thirdColumnWidth.value))) /
        100
      : usesMeasuredThirdPanelLayout
      ? (columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          (1 - getThirdPanelProgress(rightColumnWidth.value))) /
        100
      : startsWithConcurrentForwardTransition
      ? usesMeasuredInitialTransitionDimensions
        ? (columnsContainerWidth * leftColumnWidth.value) / 100
        : toTranslatePercent(leftColumnWidth.value)
      : 0,
    transform:
      usesMeasuredBackwardToSingleTransition ||
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredConcurrentForwardTransition ||
      usesMeasuredConcurrentBackwardTransition ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout
        ? [{ translateX: toTranslatePercent(0) }]
        : [
            {
              translateX: toTranslatePercent(rightColumnTranslateX.value),
            },
          ],
    opacity: rightColumnOpacity.value,
    width: usesMeasuredFourthPanelLayout
      ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
      : usesMeasuredThirdToSingleLayout
      ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
      : usesMeasuredThirdPanelLayout
      ? (columnsContainerWidth * rightColumnWidth.value) / 100
      : usesMeasuredBackwardToThirdTransition
      ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
      : usesMeasuredInitialTransitionDimensions
      ? (columnsContainerWidth * rightColumnWidth.value) / 100
      : `${rightColumnWidth.value}%`,
    overflow: 'hidden',
  }));

  const rightColumnTrailingGapAnimatedStyle = useAnimatedStyle(() => ({
    width: rightColumnTrailingGapWidth.value,
  }));

  const thirdColumnAnimatedStyle = useAnimatedStyle(() => ({
    position:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout
        ? 'absolute'
        : 'relative',
    top:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout
        ? 0
        : undefined,
    bottom:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout
        ? 0
        : undefined,
    left: usesMeasuredFourthPanelLayout
      ? (columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          (1 - getThirdPanelProgress(thirdColumnWidth.value))) /
        100
      : usesMeasuredThirdToSingleLayout
      ? (columnsContainerWidth *
          (LEFT_COLUMN_WIDTH_PERCENTAGE +
            RIGHT_COLUMN_WIDTH_PERCENTAGE * thirdToSingleProgress.value)) /
        100
      : usesMeasuredBackwardToThirdTransition
      ? (columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          getBackwardToThirdProgress(thirdColumnWidth.value)) /
        100
      : usesMeasuredThirdPanelLayout
      ? (columnsContainerWidth *
          (100 -
            RIGHT_COLUMN_WIDTH_PERCENTAGE *
              getThirdPanelProgress(rightColumnWidth.value))) /
        100
      : 0,
    opacity: thirdColumnOpacity.value,
    width: usesMeasuredFourthPanelLayout
      ? (columnsContainerWidth * thirdColumnWidth.value) / 100
      : usesMeasuredThirdToSingleLayout
      ? (columnsContainerWidth * RIGHT_COLUMN_WIDTH_PERCENTAGE) / 100
      : usesMeasuredBackwardToThirdTransition
      ? (columnsContainerWidth * thirdColumnWidth.value) / 100
      : usesMeasuredThirdPanelLayout
      ? (columnsContainerWidth * RIGHT_COLUMN_WIDTH_PERCENTAGE) / 100
      : `${thirdColumnWidth.value}%`,
    transform:
      usesMeasuredThirdToSingleLayout ||
      usesMeasuredBackwardToThirdTransition ||
      usesMeasuredFourthPanelLayout ||
      usesMeasuredThirdPanelLayout
        ? [{ translateX: toTranslatePercent(0) }]
        : [
            {
              translateX: toTranslatePercent(thirdColumnTranslateX.value),
            },
          ],
    overflow: 'hidden',
  }));

  const fourthColumnAnimatedStyle = useAnimatedStyle(() => ({
    position:
      usesMeasuredFourthPanelLayout || usesMeasuredFourthExitLayout
        ? 'absolute'
        : 'relative',
    top:
      usesMeasuredFourthPanelLayout || usesMeasuredFourthExitLayout
        ? 0
        : undefined,
    bottom:
      usesMeasuredFourthPanelLayout || usesMeasuredFourthExitLayout
        ? 0
        : undefined,
    left: usesMeasuredFourthPanelLayout
      ? (columnsContainerWidth *
          (100 -
            RIGHT_COLUMN_WIDTH_PERCENTAGE *
              getThirdPanelProgress(thirdColumnWidth.value))) /
        100
      : usesMeasuredFourthExitLayout
      ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
      : 0,
    opacity:
      usesMeasuredFourthPanelLayout || usesMeasuredFourthExitLayout
        ? 1
        : fourthColumnOpacity.value,
    width:
      usesMeasuredFourthPanelLayout || usesMeasuredFourthExitLayout
        ? (columnsContainerWidth * RIGHT_COLUMN_WIDTH_PERCENTAGE) / 100
        : `${fourthColumnWidth.value}%`,
    transform: usesMeasuredFourthPanelLayout
      ? [{ translateX: toTranslatePercent(0) }]
      : [
          {
            translateX: toTranslatePercent(fourthColumnTranslateX.value),
          },
        ],
    overflow: 'hidden',
  }));

  const exitingLeftColumnAnimatedStyle = useAnimatedStyle(() => ({
    left: usesMeasuredConcurrentForwardTransition
      ? (-columnsContainerWidth *
          LEFT_COLUMN_WIDTH_PERCENTAGE *
          clampUnit(-exitingLeftColumnTranslateX.value / 100)) /
        100
      : 0,
    transform: usesMeasuredConcurrentForwardTransition
      ? [{ translateX: toTranslatePercent(0) }]
      : [
          {
            translateX: toTranslatePercent(exitingLeftColumnTranslateX.value),
          },
        ],
    opacity: 1,
    width: usesMeasuredInitialTransitionDimensions
      ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
      : `${LEFT_COLUMN_WIDTH_PERCENTAGE}%`,
    overflow: 'hidden',
  }));

  const exitingPairedColumnsAnimatedStyle = useAnimatedStyle(() => {
    const progress = clampUnit(exitingRightColumnTranslateX.value / 100);

    return {
      left: usesMeasuredPairedConcurrentBackwardTransition
        ? columnsContainerWidth * progress
        : 0,
      transform: [{ translateX: toTranslatePercent(0) }],
      opacity: 1,
      width: usesMeasuredPairedConcurrentBackwardTransition
        ? columnsContainerWidth
        : '100%',
      overflow: 'hidden',
    };
  });

  const exitingRightColumnAnimatedStyle = useAnimatedStyle(() => ({
    left: usesMeasuredConcurrentBackwardTransition
      ? (columnsContainerWidth *
          (LEFT_COLUMN_WIDTH_PERCENTAGE +
            RIGHT_COLUMN_WIDTH_PERCENTAGE *
              clampUnit(exitingRightColumnTranslateX.value / 100))) /
        100
      : usesMeasuredBackwardToThirdTransition
      ? (columnsContainerWidth *
          (LEFT_COLUMN_WIDTH_PERCENTAGE +
            RIGHT_COLUMN_WIDTH_PERCENTAGE *
              getBackwardToThirdProgress(thirdColumnWidth.value))) /
        100
      : usesMeasuredInitialTransitionDimensions
      ? (columnsContainerWidth * LEFT_COLUMN_WIDTH_PERCENTAGE) / 100
      : `${LEFT_COLUMN_WIDTH_PERCENTAGE}%`,
    transform: usesMeasuredConcurrentBackwardTransition
      ? [{ translateX: toTranslatePercent(0) }]
      : usesMeasuredBackwardToThirdTransition
      ? [{ translateX: toTranslatePercent(0) }]
      : [
          {
            translateX: toTranslatePercent(exitingRightColumnTranslateX.value),
          },
        ],
    opacity: 1,
    width: usesMeasuredInitialTransitionDimensions
      ? (columnsContainerWidth * RIGHT_COLUMN_WIDTH_PERCENTAGE) / 100
      : `${RIGHT_COLUMN_WIDTH_PERCENTAGE}%`,
    overflow: 'hidden',
  }));

  const rightColumnPointerStyle =
    currentViewMode !== ViewMode.DUAL ? styles.noPointerEvents : null;
  const thirdColumnPointerStyle = !displayedThirdColumn
    ? styles.noPointerEvents
    : null;
  const fourthColumnPointerStyle = !deferredFourthColumn.current
    ? styles.noPointerEvents
    : null;
  useEffect(() => {
    if (!isPanelDebugEnabled()) return;

    const createPanelSlotDebug = (
      column: ColumnData | null | undefined,
      visualPosition: PanelSlotDebug['visualPosition'],
      values: Omit<PanelSlotDebug, 'panel' | 'visualPosition' | 'visible'> & {
        visible?: boolean;
      }
    ): PanelSlotDebug | undefined => {
      const panel = createPanelRefDebug(column);
      if (!panel) return undefined;

      const visible =
        values.visible ??
        (visualPosition === 'left' || visualPosition === 'right');

      return {
        visualPosition,
        visible,
        ...values,
        panel,
      };
    };

    const isThirdMode =
      isThirdPanelVisibleRef.current ||
      hasThirdColumn ||
      !!deferredThirdColumn.current;
    const hasExpandedFourthPanelForSlots =
      hasExpandedFourthPanel && !isFourthPanelDeepClose;
    const hasFourthPanelForDebug =
      hasExpandedFourthPanelForSlots || !!deferredFourthColumn.current;

    const left = createPanelSlotDebug(
      displayedLeftColumn,
      isThirdMode ? 'offscreen-left' : 'left',
      {
        widthPercent: leftColumnWidth.value,
        opacity: leftColumnOpacity.value,
        translateXPercent: leftColumnTranslateX.value,
      }
    );
    const right = createPanelSlotDebug(
      displayedRightColumn,
      !displayedRightColumn
        ? 'hidden'
        : hasExpandedFourthPanelForSlots
        ? 'hidden'
        : isThirdMode
        ? 'left'
        : startsWithBackwardToSingleTransition
        ? 'left'
        : currentViewMode === ViewMode.DUAL
        ? 'right'
        : 'offscreen-right',
      {
        widthPercent: rightColumnWidth.value,
        opacity: rightColumnOpacity.value,
        translateXPercent: rightColumnTranslateX.value,
      }
    );
    const third = createPanelSlotDebug(
      displayedThirdColumn,
      !displayedThirdColumn
        ? 'hidden'
        : hasExpandedFourthPanelForSlots
        ? 'left'
        : isThirdMode
        ? 'right'
        : 'offscreen-right',
      {
        widthPercent: thirdColumnWidth.value,
        opacity: thirdColumnOpacity.value,
        translateXPercent: thirdColumnTranslateX.value,
      }
    );
    const fourth = createPanelSlotDebug(
      deferredFourthColumn.current,
      !deferredFourthColumn.current
        ? 'hidden'
        : hasFourthPanelForDebug
        ? 'right'
        : 'offscreen-right',
      {
        widthPercent: fourthColumnWidth.value,
        opacity: fourthColumnOpacity.value,
        translateXPercent: fourthColumnTranslateX.value,
      }
    );
    const exitingLeftDebugColumn =
      exitingLeftColumn ?? exitingRightPairedLeftColumn;
    const exitingLeft = createPanelSlotDebug(
      exitingLeftDebugColumn,
      exitingRightPairedLeftColumn ? 'offscreen-right' : 'offscreen-left',
      {
        visible: true,
        widthPercent: LEFT_COLUMN_WIDTH_PERCENTAGE,
        opacity: 1,
        translateXPercent: exitingRightPairedLeftColumn
          ? exitingRightColumnTranslateX.value
          : exitingLeftColumnTranslateX.value,
      }
    );
    const exitingRight = createPanelSlotDebug(
      exitingRightColumn,
      'offscreen-right',
      {
        visible: true,
        widthPercent: RIGHT_COLUMN_WIDTH_PERCENTAGE,
        opacity: 1,
        translateXPercent: exitingRightColumnTranslateX.value,
      }
    );

    const url: GrippPanelDebugState['url'] = { pathname: location.pathname };
    if (location.search) url.search = location.search;
    if (location.hash) url.hash = location.hash;

    const slots = { left, right, third, fourth, exitingLeft, exitingRight };
    const deferredRefs = {
      right: createPanelRefDebug(deferredRightColumn.current),
      third: createPanelRefDebug(deferredThirdColumn.current),
      fourth: createPanelRefDebug(deferredFourthColumn.current),
    };
    const deferred: GrippPanelDebugState['deferred'] = {};
    if (deferredRefs.right && deferredRefs.right.id !== right?.panel.id) {
      deferred.right = deferredRefs.right;
    }
    if (deferredRefs.third && deferredRefs.third.id !== third?.panel.id) {
      deferred.third = deferredRefs.third;
    }
    if (deferredRefs.fourth && deferredRefs.fourth.id !== fourth?.panel.id) {
      deferred.fourth = deferredRefs.fourth;
    }
    const hasDeferred = Object.keys(deferred).length > 0;
    const panelSearchHints = collectPanelDebugSourceHints(
      [],
      [
        visibleColumns.left?.debug,
        visibleColumns.right?.debug,
        displayedLeftColumn?.debug,
        displayedRightColumn?.debug,
        exitingLeftColumn?.debug,
        exitingRightPairedLeftColumn?.debug,
        exitingRightColumn?.debug,
        displayedThirdColumn?.debug,
        deferredFourthColumn.current?.debug,
      ],
      [
        'gripp-workspace/libs/shared-ui/src/components/slide-layout/SlideLayout.tsx',
      ]
    );

    const debugState: GrippPanelDebugState = {
      version: 1,
      capturedAt: new Date().toISOString(),
      url,
      router: { transitionKey },
      layout: {
        currentViewMode,
        requestedViewMode: viewModeProp,
        hasRightColumn: !!rightColumn,
        hasThirdColumn,
        hasFourthColumn,
        isThirdPanelVisible: isThirdPanelVisibleRef.current,
        isFourthPanelVisible: isFourthPanelVisibleRef.current,
      },
      slots,
      ...(hasDeferred ? { deferred } : {}),
      codeSearchHints: {
        exactFilesToOpen: panelSearchHints.exactFilesToOpen,
        exactIdsToSearch: Array.from(
          new Set(
            [
              ...panelSearchHints.exactIdsToSearch,
              'SlideLayout',
              transitionKey,
              left?.panel.id,
              right?.panel.id,
              third?.panel.id,
              fourth?.panel.id,
              exitingLeft?.panel.id,
              exitingRight?.panel.id,
            ].filter((id): id is string => !!id)
          )
        ),
      },
      debug: { layoutInstanceId: layoutDebugId },
    };

    latestPanelDebugStateRef.current = debugState;
    publishPanelDebugSnapshot(debugState);
  });

  return (
    <View style={styles.container}>
      {/* Child route elements registered via `useSlidePanel` render null and
          only publish their column into the outlet state above. Rendered
          here rather than inside a column's mainComponent so SlideLayout
          owns the registration plumbing end-to-end. Wrapped in the left
          column's SlideLayoutContext provider so child routes can still
          call `useSlideLayout()` (e.g. for `onSlideChange`) — matching the
          previous behavior when the Outlet lived inside the left column. */}
      {enableChildRoutes && (
        <SlideLayoutContext.Provider value={leftColumnContextValue}>
          <Outlet context={outletContextValue} />
        </SlideLayoutContext.Provider>
      )}
      <View
        style={[styles.columnsContainer, columnsContainerWebStyle]}
        testID="slide-layout"
        nativeID={layoutDebugId}
        onLayout={handleColumnsContainerLayout}
      >
        {/* Left Column */}
        {displayedLeftColumn && (
          <SlideLayoutContext.Provider value={leftColumnContextValue}>
            <Animated.View
              style={[styles.leftColumn, leftColumnAnimatedStyle, gpuLayer]}
              testID="slide-column-left"
              nativeID={`${layoutDebugId}-left`}
            >
              <View style={styles.columnContent}>
                {displayedLeftColumn.showHeader !== false && (
                  <PanelHeader
                    title={
                      displayedLeftColumn.rawTitle ??
                      t(displayedLeftHeaderTitle ?? displayedLeftColumn.title)
                    }
                    count={
                      displayedLeftHeaderCount ?? displayedLeftColumn.count
                    }
                    showBackButton={displayedLeftColumn.showBackButton}
                    onBackPress={() => handleBackPress('left')}
                    rightActions={displayedLeftColumn.headerComponent}
                    backButtonTestID="slide-back-left"
                  />
                )}
                <View style={styles.sectionContent}>
                  {displayedLeftColumn.disableScroll ? (
                    displayedLeftColumn.mainComponent
                  ) : (
                    <ScrollView
                      ref={leftScrollViewRef}
                      onScroll={handleLeftScroll}
                      scrollEventThrottle={16}
                      testID="slide-column-left-scroll"
                      nativeID={`${layoutDebugId}-left-scroll`}
                      style={[styles.scrollContainer, scrollContainerWebStyle]}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {displayedLeftColumn.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.left && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.left}
                  </View>
                )}
              </View>
              <Animated.View
                style={[styles.leftColumnGap, leftColumnGapAnimatedStyle]}
                testID="slide-column-left-gap"
              />
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

        {exitingLeftColumn && (
          <SlideLayoutContext.Provider value={leftColumnContextValue}>
            <Animated.View
              style={[
                styles.exitingLeftColumn,
                exitingLeftColumnAnimatedStyle,
                styles.noPointerEvents,
                gpuLayer,
              ]}
              testID="slide-column-exiting-left"
              nativeID={`${layoutDebugId}-exiting-left`}
            >
              <View style={styles.columnContent}>
                {exitingLeftColumn.showHeader !== false && (
                  <PanelHeader
                    title={
                      exitingLeftColumn.rawTitle ??
                      t(
                        exitingLeftPanel?.headerTitle ?? exitingLeftColumn.title
                      )
                    }
                    count={
                      exitingLeftPanel?.headerCount ?? exitingLeftColumn.count
                    }
                    showBackButton={exitingLeftColumn.showBackButton}
                    rightActions={exitingLeftColumn.headerComponent}
                  />
                )}
                <View style={styles.sectionContent}>
                  {exitingLeftColumn.disableScroll ? (
                    exitingLeftColumn.mainComponent
                  ) : (
                    <ScrollView
                      style={[styles.scrollContainer, scrollContainerWebStyle]}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {exitingLeftColumn.mainComponent}
                    </ScrollView>
                  )}
                </View>
              </View>
              <View style={styles.leftColumnGap} />
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

        {exitingRightPairedLeftColumn && exitingRightColumn && (
          <Animated.View
            style={[
              styles.exitingPairedColumns,
              exitingPairedColumnsAnimatedStyle,
              styles.noPointerEvents,
              gpuLayer,
            ]}
            testID="slide-column-exiting-pair"
          >
            <SlideLayoutContext.Provider value={leftColumnContextValue}>
              <View
                style={styles.pairedExitingLeftColumn}
                testID="slide-column-exiting-left"
                nativeID={`${layoutDebugId}-exiting-left`}
              >
                <View style={styles.columnContent}>
                  {exitingRightPairedLeftColumn.showHeader !== false && (
                    <PanelHeader
                      title={
                        exitingRightPairedLeftColumn.rawTitle ??
                        t(
                          exitingRightPanel?.pairedHeaderTitle ??
                            exitingRightPairedLeftColumn.title
                        )
                      }
                      count={
                        exitingRightPanel?.pairedHeaderCount ??
                        exitingRightPairedLeftColumn.count
                      }
                      showBackButton={
                        exitingRightPairedLeftColumn.showBackButton
                      }
                      rightActions={
                        exitingRightPairedLeftColumn.headerComponent
                      }
                    />
                  )}
                  <View style={styles.sectionContent}>
                    {exitingRightPairedLeftColumn.disableScroll ? (
                      exitingRightPairedLeftColumn.mainComponent
                    ) : (
                      <ScrollView
                        style={[
                          styles.scrollContainer,
                          scrollContainerWebStyle,
                        ]}
                        contentContainerStyle={styles.scrollContent}
                      >
                        {exitingRightPairedLeftColumn.mainComponent}
                      </ScrollView>
                    )}
                  </View>
                </View>
                <View style={styles.leftColumnGap} />
              </View>
            </SlideLayoutContext.Provider>

            <SlideLayoutContext.Provider value={rightColumnContextValue}>
              <View
                style={styles.pairedExitingRightColumn}
                testID="slide-column-exiting-right"
                nativeID={`${layoutDebugId}-exiting-right`}
              >
                <View style={styles.columnContent}>
                  {exitingRightColumn.showHeader !== false && (
                    <PanelHeader
                      title={
                        exitingRightColumn.rawTitle ??
                        t(
                          exitingRightPanel?.headerTitle ??
                            exitingRightColumn.title
                        )
                      }
                      count={
                        exitingRightPanel?.headerCount ??
                        exitingRightColumn.count
                      }
                      showBackButton={exitingRightColumn.showBackButton}
                      rightActions={exitingRightColumn.headerComponent}
                    />
                  )}
                  <View style={styles.sectionContent}>
                    {exitingRightColumn.disableScroll ? (
                      exitingRightColumn.mainComponent
                    ) : (
                      <ScrollView
                        style={[
                          styles.scrollContainer,
                          scrollContainerWebStyle,
                        ]}
                        contentContainerStyle={styles.scrollContent}
                      >
                        {exitingRightColumn.mainComponent}
                      </ScrollView>
                    )}
                  </View>
                </View>
              </View>
            </SlideLayoutContext.Provider>
          </Animated.View>
        )}

        {/* Right Column - always mounted, visibility controlled by animations */}
        <SlideLayoutContext.Provider value={rightColumnContextValue}>
          <Animated.View
            style={[
              styles.rightColumn,
              rightColumnAnimatedStyle,
              rightColumnPointerStyle,
              gpuLayer,
            ]}
            testID="slide-column-right"
            nativeID={`${layoutDebugId}-right`}
          >
            {displayedRightColumn && (
              <View style={styles.columnContent}>
                {displayedRightColumn.showHeader !== false && (
                  <PanelHeader
                    title={
                      displayedRightColumn.rawTitle ??
                      t(displayedRightHeaderTitle ?? displayedRightColumn.title)
                    }
                    count={
                      displayedRightHeaderCount ?? displayedRightColumn.count
                    }
                    showBackButton={displayedRightColumn.showBackButton}
                    onBackPress={() => handleBackPress('right')}
                    rightActions={displayedRightColumn.headerComponent}
                    backButtonTestID="slide-back-right"
                  />
                )}
                <View style={styles.sectionContent}>
                  {displayedRightColumn.disableScroll ? (
                    displayedRightColumn.mainComponent
                  ) : (
                    <ScrollView
                      ref={rightScrollViewRef}
                      onScroll={handleRightScroll}
                      scrollEventThrottle={16}
                      testID="slide-column-right-scroll"
                      nativeID={`${layoutDebugId}-right-scroll`}
                      style={[styles.scrollContainer, scrollContainerWebStyle]}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {displayedRightColumn.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.right && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.right}
                  </View>
                )}
              </View>
            )}
            <Animated.View
              style={[
                styles.rightColumnTrailingGap,
                rightColumnTrailingGapAnimatedStyle,
              ]}
              testID="slide-column-right-trailing-gap"
            />
          </Animated.View>
        </SlideLayoutContext.Provider>

        {exitingRightColumn && !exitingRightPairedLeftColumn && (
          <SlideLayoutContext.Provider value={rightColumnContextValue}>
            <Animated.View
              style={[
                styles.exitingRightColumn,
                exitingRightColumnAnimatedStyle,
                styles.noPointerEvents,
                gpuLayer,
              ]}
              testID="slide-column-exiting-right"
              nativeID={`${layoutDebugId}-exiting-right`}
            >
              <View style={styles.columnContent}>
                {exitingRightColumn.showHeader !== false && (
                  <PanelHeader
                    title={
                      exitingRightColumn.rawTitle ??
                      t(
                        exitingRightPanel?.headerTitle ??
                          exitingRightColumn.title
                      )
                    }
                    count={
                      exitingRightPanel?.headerCount ?? exitingRightColumn.count
                    }
                    showBackButton={exitingRightColumn.showBackButton}
                    rightActions={exitingRightColumn.headerComponent}
                  />
                )}
                <View style={styles.sectionContent}>
                  {exitingRightColumn.disableScroll ? (
                    exitingRightColumn.mainComponent
                  ) : (
                    <ScrollView
                      style={[styles.scrollContainer, scrollContainerWebStyle]}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {exitingRightColumn.mainComponent}
                    </ScrollView>
                  )}
                </View>
              </View>
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

        {/* Third Column - always mounted when provided, visibility controlled by animations */}
        <SlideLayoutContext.Provider value={thirdColumnContextValue}>
          <Animated.View
            style={[
              thirdColumnAnimatedStyle,
              thirdColumnPointerStyle,
              gpuLayer,
            ]}
            testID="slide-column-third"
            nativeID={`${layoutDebugId}-third`}
          >
            {displayedThirdColumn && (
              <View style={[styles.columnContent, styles.thirdColumnContent]}>
                {displayedThirdColumn.showHeader !== false && (
                  <PanelHeader
                    title={
                      displayedThirdColumn.rawTitle ??
                      t(columnTitles.third ?? displayedThirdColumn.title)
                    }
                    count={columnCounts.third ?? displayedThirdColumn.count}
                    showBackButton={displayedThirdColumn.showBackButton}
                    onBackPress={() => handleBackPress('third')}
                    rightActions={displayedThirdColumn.headerComponent}
                    backButtonTestID="slide-back-third"
                  />
                )}
                <View style={styles.sectionContent}>
                  {displayedThirdColumn.disableScroll ? (
                    displayedThirdColumn.mainComponent
                  ) : (
                    <ScrollView
                      ref={thirdScrollViewRef}
                      onScroll={handleThirdScroll}
                      scrollEventThrottle={16}
                      testID="slide-column-third-scroll"
                      nativeID={`${layoutDebugId}-third-scroll`}
                      style={[styles.scrollContainer, scrollContainerWebStyle]}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {displayedThirdColumn.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.third && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.third}
                  </View>
                )}
              </View>
            )}
          </Animated.View>
        </SlideLayoutContext.Provider>

        {/* Fourth Column - always mounted when provided, visibility controlled by animations */}
        <SlideLayoutContext.Provider value={fourthColumnContextValue}>
          <Animated.View
            style={[
              fourthColumnAnimatedStyle,
              fourthColumnPointerStyle,
              gpuLayer,
            ]}
            testID="slide-column-fourth"
            nativeID={`${layoutDebugId}-fourth`}
          >
            {deferredFourthColumn.current && (
              <View style={[styles.columnContent, styles.fourthColumnContent]}>
                {deferredFourthColumn.current.showHeader !== false && (
                  <PanelHeader
                    title={
                      deferredFourthColumn.current.rawTitle ??
                      t(
                        columnTitles.fourth ??
                          deferredFourthColumn.current.title
                      )
                    }
                    count={
                      columnCounts.fourth ?? deferredFourthColumn.current.count
                    }
                    showBackButton={deferredFourthColumn.current.showBackButton}
                    onBackPress={() => {
                      deferredFourthColumn.current?.onBackButtonPress?.();
                    }}
                    rightActions={deferredFourthColumn.current.headerComponent}
                    backButtonTestID="slide-back-fourth"
                  />
                )}
                <View style={styles.sectionContent}>
                  {deferredFourthColumn.current?.disableScroll ? (
                    deferredFourthColumn.current.mainComponent
                  ) : (
                    <ScrollView
                      style={[styles.scrollContainer, scrollContainerWebStyle]}
                      contentContainerStyle={styles.scrollContent}
                    >
                      {deferredFourthColumn.current?.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.fourth && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.fourth}
                  </View>
                )}
              </View>
            )}
          </Animated.View>
        </SlideLayoutContext.Provider>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  columnsContainer: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  leftColumn: {
    flexDirection: 'row',
  },
  exitingLeftColumn: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 2,
    flexDirection: 'row',
  },
  exitingPairedColumns: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 2,
    flexDirection: 'row',
  },
  pairedExitingLeftColumn: {
    flex: 1,
    flexDirection: 'row',
  },
  pairedExitingRightColumn: {
    flexShrink: 0,
    width: `${RIGHT_COLUMN_WIDTH_PERCENTAGE}%`,
  },
  leftColumnGap: {
    flexShrink: 0,
    width: LEFT_COLUMN_GAP,
  },
  rightColumn: {
    flexDirection: 'row',
  },
  rightColumnTrailingGap: {
    flexShrink: 0,
    width: 0,
  },
  exitingRightColumn: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `${LEFT_COLUMN_WIDTH_PERCENTAGE}%`,
    zIndex: 2,
  },
  thirdColumnContent: {
    paddingLeft: LEFT_COLUMN_GAP,
    paddingRight: LEFT_COLUMN_GAP,
  },
  fourthColumnContent: {
    paddingLeft: LEFT_COLUMN_GAP,
    paddingRight: LEFT_COLUMN_GAP,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  columnContent: {
    flex: 1,
  },
  sectionContent: {
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: Colors.brand.white,
    flexGrow: 1,
    overflow: 'hidden',
    height: 600,
    flex: 1,
  },
  columnOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  noPointerEvents: {
    pointerEvents: 'none',
  },
});
